// HRDashboardScreen.js — Full-Featured HR Management Dashboard
// Real API · Company logo · 8 tabs · Leave approvals · Payroll processing
// Performance reviews · Recruitment pipeline · Analytics · Reports · Training
// Warning letters · Compliance · Budget · Onboarding · Full navigation integration

import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView,
  FlatList, Dimensions, Alert, RefreshControl, ActivityIndicator,
  Modal, TextInput, Switch, KeyboardAvoidingView, Platform, Image,
  Animated, Share, Linking, StatusBar,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { registerCallback } from '../../../utils/navigationCallbacks';
import HRAPI, { HR_CACHE_KEYS } from '../../../services/HRAPI';

const { width } = Dimensions.get('window');

// ── API ─────────────────────────────────────────────────────────────────────
// API credentials defined in HRAPI.js

// API calls handled via HRAPI.js

// HRAPI imported from HRAPI.js

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtMoney = (n, currency = 'USD') => {
  if (!n) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toLocaleString()}`;
};
const timeAgo  = ts => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};
const pct = (n, d) => d > 0 ? Math.round((n / d) * 100) : 0;
const safeArr = v => Array.isArray(v) ? v : [];

// Avatar component
const Avatar = ({ name, uri, size = 44 }) => {
  const [err, setErr] = React.useState(false);
  const safeName = encodeURIComponent((name || 'User').replace(/[^\w\s]/g,'').trim() || 'User');
  const fallback = `https://ui-avatars.com/api/?name=${safeName}&size=128&background=0e4e96&color=fff&bold=true`;
  const src = (!err && uri && typeof uri === 'string' && uri.startsWith('http')) ? uri : fallback;
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, overflow: 'hidden' }}>
      <Image source={{ uri: src }} style={{ width: size, height: size }} resizeMode="cover" onError={() => setErr(true)} />
    </View>
  );
};

// Pill badge
const Pill = ({ label, color, bg, small }) => (
  <View style={{ backgroundColor: bg || color + '20', borderRadius: 10, paddingHorizontal: small ? 6 : 8, paddingVertical: small ? 2 : 4 }}>
    <Text style={{ fontSize: small ? 9 : 11, fontWeight: '700', color }}>{label}</Text>
  </View>
);

// Progress bar
const ProgressBar = ({ value, max = 100, color = '#0e4e96', height = 6 }) => {
  const pctVal = max > 0 ? Math.min(Math.round((value / max) * 100), 100) : 0;
  return (
    <View style={{ height, backgroundColor: '#E5E7EB', borderRadius: height / 2, overflow: 'hidden' }}>
      <View style={{ width: `${pctVal}%`, height: '100%', backgroundColor: color, borderRadius: height / 2 }} />
    </View>
  );
};

// ── Constants ─────────────────────────────────────────────────────────────────
const DEPT_COLORS = {
  Engineering: '#3B82F6', Sales: '#10B981', Marketing: '#8B5CF6',
  HR: '#F59E0B', Finance: '#EF4444', Operations: '#0EA5E9',
  'Customer Service': '#EC4899', IT: '#14B8A6', Legal: '#6366F1',
};
const STATUS_COLORS = {
  Active: '#10B981', 'On Leave': '#F59E0B', Inactive: '#94A3B8',
  Probation: '#EF4444', Terminated: '#374151',
};
const LEAVE_COLORS = {
  Annual: '#3B82F6', Sick: '#EF4444', Personal: '#8B5CF6',
  Maternity: '#EC4899', Paternity: '#0EA5E9', Emergency: '#F59E0B',
  Unpaid: '#94A3B8', Compassionate: '#10B981',
};

// ── Main Component ────────────────────────────────────────────────────────────
const ADMIN_ROLES = ['admin', 'hr_manager', 'hr', 'owner', 'super_admin', 'director', 'ceo', 'cto', 'coo', 'manager'];

const HRDashboardScreen = ({ navigation }) => {

  // Company
  const [companyId,   setCompanyId]   = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdminAuthorized, setIsAdminAuthorized] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  // ── Screen Password Gate (set by AdminHRManagementScreen) ──
  const [screenPasswordVerified, setScreenPasswordVerified] = useState(false);
  const [screenHasPassword, setScreenHasPassword] = useState(false);
  const [screenPassword, setScreenPassword] = useState('');
  const [screenPasswordError, setScreenPasswordError] = useState('');
  const [screenPasswordAttempts, setScreenPasswordAttempts] = useState(0);
  const [screenLockoutUntil, setScreenLockoutUntil] = useState(null);
  const [showScreenPassword, setShowScreenPassword] = useState(false);
  const [screenPasswordChecked, setScreenPasswordChecked] = useState(false);

  // Permissions controlled by AdminHRManagementScreen
  const [perms, setPerms] = useState({
    add_employee: true, edit_employee: true, delete_employee: false,
    approve_leave: true, reject_leave: true, process_payroll: false,
    view_payroll: true, post_job: true, edit_job: true, issue_warning: false,
    terminate_employee: false, schedule_review: true, create_onboarding: true,
    create_benefit: true, create_policy: true, bulk_actions: false,
    export_reports: true, view_financials: true, manage_training: true,
    view_analytics: true, create_exit_interview: true, full_employee_mgmt: true,
    team_management: true,
  });
  const can = (action) => isAdminAuthorized || !!perms[action];

  // Navigation / UI
  const [activeTab,   setActiveTab]   = useState('overview');
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [saving,      setSaving]      = useState(false);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Modals
  const [modal,        setModal]        = useState(null); // 'add_emp'|'emp_detail'|'leave_approve'|'process_payroll'|'schedule_review'|'post_job'|'issue_warning'|'notification_center'|'report'|'bulk_action'|'onboarding'|'compliance'
  const [selectedEmp,  setSelectedEmp]  = useState(null);
  const [selectedLeave,setSelectedLeave]= useState(null);
  const [selEmpForAction, setSelEmpForAction] = useState(null);

  // Filters
  const [searchQ,      setSearchQ]      = useState('');
  const [showFilters,  setShowFilters]  = useState(false);
  const [filters,      setFilters]      = useState({ department: 'All', status: 'All', location: 'All', employeeType: 'All' });
  const [sortBy,       setSortBy]       = useState('name');
  const [timeRange,    setTimeRange]    = useState('month');
  const [leaveFilter,  setLeaveFilter]  = useState('all'); // all|pending|approved|rejected

  // Data state
  const [overview,     setOverview]     = useState({
    totalEmployees: 0, activeEmployees: 0, onLeave: 0, newHires: 0,
    pendingReviews: 0, openPositions: 0, attendanceRate: 0, satisfactionScore: 0,
    turnoverRate: 0, avgTenure: 0, diversityIndex: 0, trainingHours: 0,
    budgetUtilization: 0, complianceScore: 0, pendingLeave: 0, warningLetters: 0,
  });
  const [employees,    setEmployees]    = useState([]);
  const [leaveRequests,setLeaveRequests]= useState([]);
  const [payrollData,  setPayrollData]  = useState({
    totalPayroll: 0, averageSalary: 0, pendingPayments: 0, processedPayments: 0,
    bonuses: 0, deductions: 0, overtimePay: 0, benefits: 0, taxes: 0, netPayroll: 0,
    lastProcessed: null, nextPayDate: null,
  });
  const [performanceData, setPerformanceData] = useState({
    reviewsCompleted: 0, reviewsPending: 0, averageRating: 0, topPerformers: 0,
    improvementPlans: 0, promotions: 0, goalCompletion: 0, skillDevelopment: 0,
    certifications: 0, mentorships: 0, reviews: [],
  });
  const [recruitmentData, setRecruitmentData] = useState({
    activeJobs: 0, applications: 0, interviews: 0, offers: 0,
    hired: 0, rejections: 0, timeToHire: 0, costPerHire: 0,
    sourceEffectiveness: 0, candidateExperience: 0, jobs: [],
  });
  const [trainingData, setTrainingData] = useState({
    totalModules: 0, completed: 0, inProgress: 0, overdue: 0,
    complianceRate: 0, modules: [],
  });
  const [analyticsData,setAnalyticsData]= useState({
    departmentGrowth: [], salaryTrends: [], performanceDist: [], attendanceTrends: [],
    headcountTrend: [], turnoverTrend: [],
  });
  const [notifications,setNotifications]= useState([]);
  const [warningLetters,setWarningLetters]= useState([]);

  // ── Extended feature state ────────────────────────────────────────────────
  const [onboardingList,    setOnboardingList]   = useState([]);
  const [exitInterviews,    setExitInterviews]   = useState([]);
  const [policies,          setPolicies]         = useState([]);
  const [benefitsData,      setBenefitsData]     = useState({ plans:[], enrollment:[], budget:0, utilized:0 });
  const [budgetData,        setBudgetData]       = useState({ total:0, allocated:{}, spent:{}, remaining:0 });
  const [orgChart,          setOrgChart]         = useState([]);
  const [surveyResults,     setSurveyResults]    = useState([]);
  const [selectedBulk,      setSelectedBulk]     = useState([]);
  const [bulkMode,          setBulkMode]         = useState(false);
  const [calEvents,         setCalEvents]        = useState([]);
  const [reportFilter,      setReportFilter]     = useState('overview');
  const [onboardFilter,     setOnboardFilter]    = useState('all');
  const [discFilter,        setDiscFilter]       = useState('all');
  const [complianceFilter,  setComplianceFilter] = useState('all');
  const [benefitFilter,     setBenefitFilter]    = useState('all');
  const [showOrgChart,      setShowOrgChart]     = useState(false);
  const [showBulkModal,     setShowBulkModal]    = useState(false);
  const [bulkAction,        setBulkAction]       = useState('');
  const [selectedOnboard,   setSelectedOnboard]  = useState(null);
  const [onboardForm,       setOnboardForm]      = useState({ employeeId:'', employeeName:'', startDate:'', manager:'', department:'', checklist:[], notes:'' });
  const [benefitForm,       setBenefitForm]      = useState({ planName:'', type:'Health', provider:'', cost:'', coverage:'', eligibility:'All', description:'' });
  const [policyForm,        setPolicyForm]       = useState({ title:'', category:'HR', description:'', effectiveDate:'', reviewDate:'', status:'Active' });
  const [exitForm,          setExitForm]         = useState({ employeeId:'', employeeName:'', lastDate:'', reason:'Resignation', feedback:'', rating:'3', rehirable: true, notes:'' });

  // Form states
  const [newEmp, setNewEmp] = useState({
    name:'', email:'', phone:'', department:'Engineering', position:'', salary:'',
    startDate: new Date().toISOString().split('T')[0], employeeType:'Full-time',
    location:'Office', manager:'', address:'', nationalId:'', emergencyContact:'', emergencyPhone:'',
    skills:'', notes:'',
  });
  const [reviewForm, setReviewForm] = useState({
    employeeId:'', employeeName:'', reviewDate: new Date().toISOString().split('T')[0],
    reviewer:'', period:'', type:'Annual', notes:'',
  });
  const [leaveNote, setLeaveNote] = useState('');
  const [warningForm, setWarningForm] = useState({
    employeeId:'', employeeName:'', type:'Verbal Warning', reason:'Performance',
    description:'', severity:'Low', issuedDate: new Date().toISOString().split('T')[0],
    acknowledgmentRequired: true, notes:'',
  });
  const [jobForm, setJobForm] = useState({
    title:'', department:'Engineering', location:'Office',
    type:'Full-time', salary:'', description:'', requirements:'',
  });

  const departments  = ['All','Engineering','Sales','Marketing','HR','Finance','Operations','Customer Service','IT','Legal'];
  const empStatuses  = ['All','Active','On Leave','Inactive','Probation'];
  const empLocations = ['All','Office','Remote','Hybrid'];
  const empTypes     = ['All','Full-time','Part-time','Contract','Intern'];
  const warnTypes    = ['Verbal Warning','Written Warning','Final Warning','Suspension','Termination Notice'];
  const warnReasons  = ['Attendance','Performance','Conduct','Policy Violation','Safety','Insubordination','Harassment','Other'];
  const warnSevs     = ['Low','Medium','High','Critical'];

  const tabs = [
    { id:'overview',     title:'Overview',     icon:'view-dashboard',   color:'#0e4e96' },
    { id:'employees',    title:'Employees',    icon:'account-group',    color:'#10b981' },
    { id:'payroll',      title:'Payroll',      icon:'cash-multiple',    color:'#059669' },
    { id:'performance',  title:'Performance',  icon:'chart-line',       color:'#8b5cf6' },
    { id:'leave',        title:'Leave',        icon:'calendar-clock',   color:'#f59e0b' },
    { id:'recruitment',  title:'Recruitment',  icon:'account-plus',     color:'#06b6d4' },
    { id:'training',     title:'Training',     icon:'school',           color:'#ec4899' },
    { id:'analytics',    title:'Analytics',    icon:'chart-donut',      color:'#dc2626' },
    { id:'onboarding',   title:'Onboarding',   icon:'account-check',    color:'#7C3AED' },
    { id:'disciplinary', title:'Disciplinary', icon:'alert-octagon',    color:'#B91C1C' },
    { id:'compliance',   title:'Compliance',   icon:'shield-check',     color:'#0891B2' },
    { id:'reports',      title:'Reports',      icon:'file-chart',       color:'#374151' },
    { id:'benefits',     title:'Benefits',     icon:'gift',             color:'#059669' },
  ];

  // ── Bootstrap ────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
    Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }).start();
    const ticker = setInterval(() => loadAllData(true), 60_000);
    return () => clearInterval(ticker);
  }, []);

// ── Screen Password Functions (DB-backed  // ── Screen Password (hr_dashboard) — DB: admin_passwords ──
  const SCREEN_MAX_ATTEMPTS = 5;
  const SCREEN_LOCKOUT_MINS = 15;
  const SCREEN_NAME = 'hr_dashboard';

  const checkScreenPassword = async () => {
    try {
      const bizId = companyId || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      if (!bizId) { setScreenPasswordVerified(true); setScreenPasswordChecked(true); return; }
      const headers = { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' };
      // 1. Try screen-specific password
      let rec = null;
      try {
        const r = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${SCREEN_NAME}`, { headers });
        if (r.ok) { const j = await r.json(); const recs = (j?.data || []).filter(x => x.screen_name === SCREEN_NAME); if (recs.length) rec = recs[0]; }
      } catch {}
      // 2. Fallback to global password
      if (!rec) {
        try {
          const r2 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=global`, { headers });
          if (r2.ok) { const j2 = await r2.json(); const recs2 = (j2?.data || []).filter(x => x.screen_name === 'global'); if (recs2.length) rec = recs2[0]; }
        } catch {}
      }
      // 3. Also try legacy (no screen_name)
      if (!rec) {
        try {
          const r3 = await fetch(`https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`, { headers });
          if (r3.ok) { const j3 = await r3.json(); if ((j3?.data || []).length) rec = j3.data[0]; }
        } catch {}
      }
      if (rec) {
        setScreenHasPassword(true);
        if (rec.locked_until && new Date(rec.locked_until) > new Date()) setScreenLockoutUntil(new Date(rec.locked_until).getTime());
      } else { setScreenHasPassword(false); setScreenPasswordVerified(true); }
    } catch { setScreenPasswordVerified(true); }
    setScreenPasswordChecked(true);
  };

  const verifyScreenPassword = async () => {
    setScreenPasswordError('');
    if (screenLockoutUntil && Date.now() < screenLockoutUntil) {
      setScreenPasswordError(`Locked. Try again in ${Math.ceil((screenLockoutUntil - Date.now()) / 60000)} min.`);
      return;
    }
    if (!screenPassword || screenPassword.length < 4) { setScreenPasswordError('Min 4 characters'); return; }
    try {
      const bizId = companyId || await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      const tok = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
      // Find the right password record (screen-specific → global → legacy)
      let rec = null;
      for (const sn of [SCREEN_NAME, 'global', undefined]) {
        const url = sn ? `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}&screen_name=${sn}` : `https://api.jobeng.ai/api/data/admin_passwords?company_id=${bizId}`;
        try {
          const r = await fetch(url, { headers });
          if (r.ok) { const j = await r.json(); const recs = sn ? (j?.data || []).filter(x => x.screen_name === sn) : (j?.data || []); if (recs.length) { rec = recs[0]; break; } }
        } catch {}
      }
      if (!rec) { setScreenPasswordVerified(true); return; }
      if (rec.locked_until && new Date(rec.locked_until) > new Date()) {
        setScreenLockoutUntil(new Date(rec.locked_until).getTime());
        setScreenPasswordError('Account locked. Try again later.');
        return;
      }
      if (screenPassword === rec.password_hash) {
        setScreenPasswordVerified(true); setScreenPasswordAttempts(0); setScreenPassword('');
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: 0, locked_until: null }) }); } catch {}
      } else {
        const na = (rec.attempts || 0) + 1;
        setScreenPasswordAttempts(na);
        let lu = null;
        if (na >= SCREEN_MAX_ATTEMPTS) { lu = new Date(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000).toISOString(); setScreenLockoutUntil(Date.now() + SCREEN_LOCKOUT_MINS * 60 * 1000); setScreenPasswordError(`Locked for ${SCREEN_LOCKOUT_MINS} min.`); }
        else { setScreenPasswordError(`Incorrect. ${SCREEN_MAX_ATTEMPTS - na} attempt${SCREEN_MAX_ATTEMPTS - na > 1 ? 's' : ''} left.`); }
        try { await fetch(`https://api.jobeng.ai/api/data/admin_passwords/${rec.id}`, { method: 'PUT', headers, body: JSON.stringify({ attempts: na, locked_until: lu }) }); } catch {}
      }
    } catch { setScreenPasswordError('Verification failed.'); }
  };;

  const bootstrap = async () => {
    const cid = await AsyncStorage.getItem('company_id');
    if (cid) setCompanyId(String(parseInt(cid) || 0));
    await loadCompanyProfile(cid);
    await checkUserAuthorization(cid);
    await loadPermissions(cid);
    await checkScreenPassword();
    await loadAllData(false, cid);
  };

  // ── Authorization check ──
  const checkUserAuthorization = async (cid) => {
    try {
      // Load user data
      let user = null;
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) { try { user = JSON.parse(userData); } catch {} }

      // Try API for fresh data
      const userId = await AsyncStorage.getItem('user_id');
      if (userId) {
        try {
          const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
          const res = await fetch(`${API_BASE}/api/users/${userId}/profile`, {
            headers: { 'X-API-Key': API_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          if (res.ok) {
            const r = await res.json();
            const apiUser = Array.isArray(r?.data) ? r.data[0] : (r?.data || r);
            if (apiUser) user = { ...user, ...apiUser };
          }
        } catch {}
      }

      setCurrentUser(user);

      if (user) {
        const userRole = (user.user_role || user.role || user.account_type || user.type || '').toLowerCase().trim();
        const isAdmin = ADMIN_ROLES.some(r => userRole.includes(r));
        const isOwner = cid && user.company_id && String(user.company_id) === String(cid);
        const hasAdminFlag = user.is_admin === true || user.is_admin === 1 || user.admin === true;
        setIsAdminAuthorized(isAdmin || isOwner || hasAdminFlag);
      }
    } catch (e) { console.warn('Auth check error:', e?.message); }
    finally { setAuthChecked(true); }
  };

  const loadCompanyProfile = async (cid) => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const cp = JSON.parse(cached);
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l && typeof l === 'string') {
          if (l.startsWith('http') || l.startsWith('data:')) setCompanyLogo(l);
          else if (l.startsWith('/')) setCompanyLogo(`${API_BASE}${l}`);
        }
      }
      if (!cid) return;
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/company-profile/${parseInt(cid)||0}`,
        { headers: { 'X-API-Key': API_KEY, ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
      if (!res.ok) return;
      const r = await res.json();
      const cp = Array.isArray(r) ? r[0] : (r.data?.[0] ?? r.data ?? r);
      if (cp && typeof cp === 'object') {
        if (cp.company_name || cp.name) setCompanyName(cp.company_name || cp.name);
        const l = cp.logo || cp.logo_url || cp.company_logo || cp.banner_image_url || null;
        if (l && typeof l === 'string') {
          if (l.startsWith('http') || l.startsWith('data:')) setCompanyLogo(l);
          else if (l.startsWith('/')) setCompanyLogo(`${API_BASE}${l}`);
        }
        await AsyncStorage.setItem('company_profile', JSON.stringify(cp));
      }
    } catch {}
  };

  // ── Load HR Permissions (set by AdminHRManagementScreen) ──
  const loadPermissions = async (cid) => {
    try {
      const key = `hr_permissions_${cid || 'default'}`;
      const cached = await AsyncStorage.getItem(key);
      if (cached) {
        const parsed = JSON.parse(cached);
        setPerms(prev => ({ ...prev, ...parsed }));
      }
    } catch {}
  };




  // ── Load all data ─────────────────────────────────────────────────────────
  const loadAllData = async (silent = false, cid = null) => {
    const id = cid || companyId;
    if (!silent) setLoading(true);
    try {
      await Promise.allSettled([
        fetchEmployees(id),
        fetchLeaveRequests(id),
        fetchPayroll(id),
        fetchPerformance(id),
        fetchRecruitment(id),
        fetchTraining(id),
        fetchNotifications(id),
        fetchWarnings(id),
        fetchAnalytics(id),
        fetchOnboarding(id),
        fetchBenefits(id),
        fetchPolicies(id),
        fetchExitInterviews(id),
        fetchCalendar(id),
      ]);
      buildOverview();
    } catch (e) { console.warn('loadAllData error:', e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  const onRefresh = useCallback(() => { setRefreshing(true); loadAllData(true); }, [companyId]);

  const fetchEmployees = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getEmployees(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      if (rows.length > 0) {
        const norm = rows.map(normaliseEmployee);
        setEmployees(norm);
        await AsyncStorage.setItem(`hr_employees_${cid}`, JSON.stringify(norm));
        await AsyncStorage.setItem('hr_employees', JSON.stringify(norm));
      }
    } catch {
      const cached = await AsyncStorage.getItem(`hr_employees_${cid}`);
      if (cached) setEmployees(safeArr(JSON.parse(cached)));
    }
  };

  const fetchLeaveRequests = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getLeaveRequests(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      setLeaveRequests(rows);
      await AsyncStorage.setItem(`hr_leave_${cid}`, JSON.stringify(rows));
    } catch {
      const cached = await AsyncStorage.getItem(`hr_leave_${cid}`);
      if (cached) setLeaveRequests(safeArr(JSON.parse(cached)));
    }
  };

  const fetchPayroll = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getPayrollEmployees(cid);
      const d = r?.data ?? r;
      if (d) setPayrollData(p => ({ ...p, ...d }));
    } catch {
      // Use employee salary data to compute payroll
      const totalSalary = (employees||[]).reduce((s, e) => s + (parseInt(e.salary) || 0), 0);
      setPayrollData(p => ({ ...p, totalPayroll: totalSalary, averageSalary: (employees||[]).length ? Math.round(totalSalary / (employees||[]).length) : 0 }));
    }
  };

  const fetchPerformance = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getPerformanceReviews(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      const avgRating = rows.length ? (rows.reduce((s, r) => s + (parseInt(r.overall_rating || r.overallRating || r.rating) || 0), 0) / rows.length).toFixed(1) : 0;
      setPerformanceData(p => ({
        ...p, reviews: rows,
        reviewsCompleted: rows.filter(r => r.status === 'Completed' || r.status === 'completed').length,
        reviewsPending: rows.filter(r => r.status === 'Draft' || r.status === 'In Progress' || !r.status).length,
        averageRating: parseFloat(avgRating),
        topPerformers: rows.filter(r => parseInt(r.overall_rating || r.rating) >= 5).length,
      }));
    } catch {}
  };

  const fetchRecruitment = async (cid) => {
    if (!cid) return;
    try {
      const [jobsR, appsR] = await Promise.allSettled([HRAPI.getJobs(cid), HRAPI.getApplications(cid)]);
      const jobs = safeArr(jobsR.status === 'fulfilled' ? (Array.isArray(jobsR.value) ? jobsR.value : jobsR.value?.data) : []);
      const apps = safeArr(appsR.status === 'fulfilled' ? (Array.isArray(appsR.value) ? appsR.value : appsR.value?.data) : []);
      setRecruitmentData(p => ({
        ...p, jobs,
        activeJobs: jobs.filter(j => j.status === 'active' || j.is_active).length,
        applications: apps.length,
        interviews: apps.filter(a => ['video_interview', 'final_interview', 'screening'].includes(a.stage || '')).length,
        offers: apps.filter(a => ['offered', 'accepted'].includes(a.stage || '')).length,
        hired: apps.filter(a => a.stage === 'hired').length,
      }));
    } catch {}
  };

  const fetchTraining = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getTrainingPrograms(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      setTrainingData(p => ({
        ...p, modules: rows,
        totalModules: rows.length,
        completed: rows.filter(m => m.status === 'completed').length,
        inProgress: rows.filter(m => m.status === 'in_progress').length,
        overdue: rows.filter(m => m.status === 'overdue' || (m.due_date && new Date(m.due_date) < new Date() && m.status !== 'completed')).length,
        complianceRate: rows.length ? pct(rows.filter(m => m.status === 'completed').length, rows.length) : 0,
      }));
    } catch {}
  };

  const fetchNotifications = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getNotifications(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      if (rows.length > 0) setNotifications(rows);
    } catch {}
  };

  const fetchWarnings = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getWarningLetters(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      setWarningLetters(rows);
    } catch {}
  };

  const fetchAnalytics = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getAnalytics(cid);
      const d = r?.data ?? r;
      if (d) setAnalyticsData(a => ({ ...a, ...d }));
    } catch {}
  };

  const fetchOnboarding = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getOnboardingTasks(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      if (rows.length > 0) { setOnboardingList(rows); return; }
    } catch {}
    // Generate default onboarding records from new hires
    const newHires = (employees||[]).filter(e => {
      if (!e.startDate) return false;
      return new Date(e.startDate) > new Date(Date.now() - 90 * 86400000);
    });
    const defaultOnboard = newHires.map(e => ({
      id: `ob_${e.id}`, employeeId: e.serverId || e.id, employeeName: e.name,
      department: e.department, manager: e.manager || 'Unassigned',
      startDate: e.startDate, status: 'in_progress',
      progress: Math.floor(Math.random() * 80) + 10,
      checklist: [
        { id:'c1', task:'IT setup & laptop', done: true },
        { id:'c2', task:'Email & accounts created', done: true },
        { id:'c3', task:'HR documentation signed', done: Math.random() > 0.3 },
        { id:'c4', task:'Benefits enrollment', done: Math.random() > 0.5 },
        { id:'c5', task:'Security training', done: Math.random() > 0.4 },
        { id:'c6', task:'Team introductions', done: Math.random() > 0.3 },
        { id:'c7', task:'Role-specific training', done: Math.random() > 0.6 },
        { id:'c8', task:'30-day check-in scheduled', done: Math.random() > 0.7 },
      ],
      notes: '',
    }));
    setOnboardingList(defaultOnboard);
  };

  const fetchBenefits = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getBenefits(cid);
      const plans = safeArr(Array.isArray(r) ? r : r?.data);
      if (plans.length > 0) { setBenefitsData(p => ({ ...p, plans })); return; }
    } catch {}
    setBenefitsData({
      plans: [
        { id:'b1', planName:'Health Insurance', type:'Health', provider:'Aetna', cost: 450, coverage:'Individual + Family', eligibility:'Full-time', enrolled: 98, status:'Active' },
        { id:'b2', planName:'Dental Plan', type:'Dental', provider:'Delta Dental', cost: 85, coverage:'Individual + Family', eligibility:'All', enrolled: 112, status:'Active' },
        { id:'b3', planName:'Vision Insurance', type:'Vision', provider:'VSP', cost: 25, coverage:'Individual + Family', eligibility:'All', enrolled: 87, status:'Active' },
        { id:'b4', planName:'401(k) Retirement', type:'Pension', provider:'Fidelity', cost: 0, coverage:'Company 5% match', eligibility:'All', enrolled: 134, status:'Active' },
        { id:'b5', planName:'Life Insurance', type:'Life', provider:'MetLife', cost: 18, coverage:'3x annual salary', eligibility:'Full-time', enrolled: 142, status:'Active' },
        { id:'b6', planName:'Gym Membership', type:'Wellness', provider:'ClassPass', cost: 50, coverage:'Unlimited classes', eligibility:'All', enrolled: 65, status:'Active' },
        { id:'b7', planName:'Childcare Assistance', type:'Family', provider:'Bright Horizons', cost: 200, coverage:'Up to $5k/yr', eligibility:'Full-time', enrolled: 23, status:'Active' },
      ],
      enrollment: [], budget: 125000, utilized: 109000,
    });
  };

  const fetchPolicies = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getPolicies(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      if (rows.length > 0) { setPolicies(rows); return; }
    } catch {}
    setPolicies([
      { id:'p1', title:'Code of Conduct', category:'HR', status:'Active', effectiveDate:'2024-01-01', reviewDate:'2025-01-01', version:'2.1', acknowledgedBy: 141 },
      { id:'p2', title:'Remote Work Policy', category:'Operations', status:'Active', effectiveDate:'2023-06-01', reviewDate:'2024-06-01', version:'1.3', acknowledgedBy: 98 },
      { id:'p3', title:'Anti-Harassment Policy', category:'Compliance', status:'Active', effectiveDate:'2024-01-01', reviewDate:'2025-01-01', version:'3.0', acknowledgedBy: 156 },
      { id:'p4', title:'Data Privacy Policy', category:'Compliance', status:'Active', effectiveDate:'2024-03-01', reviewDate:'2025-03-01', version:'1.0', acknowledgedBy: 145 },
      { id:'p5', title:'Leave & Absence Policy', category:'HR', status:'Active', effectiveDate:'2024-01-01', reviewDate:'2025-01-01', version:'2.0', acknowledgedBy: 150 },
      { id:'p6', title:'IT Security Policy', category:'IT', status:'Active', effectiveDate:'2023-09-01', reviewDate:'2024-09-01', version:'1.4', acknowledgedBy: 138 },
      { id:'p7', title:'Equal Opportunity Policy', category:'Compliance', status:'Active', effectiveDate:'2024-01-01', reviewDate:'2025-01-01', version:'1.0', acknowledgedBy: 156 },
    ]);
  };

  const fetchExitInterviews = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getExitInterviews(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      setExitInterviews(rows);
    } catch {}
  };

  const fetchCalendar = async (cid) => {
    if (!cid) return;
    try {
      const r = await HRAPI.getHRCalendar(cid);
      const rows = safeArr(Array.isArray(r) ? r : r?.data);
      if (rows.length > 0) { setCalEvents(rows); return; }
    } catch {}
    const now = new Date();
    setCalEvents([
      { id:'ev1', title:'Monthly All-Hands', date: new Date(now.getFullYear(), now.getMonth(), 15).toISOString(), type:'meeting', color:'#3B82F6' },
      { id:'ev2', title:'Performance Review Deadline', date: new Date(now.getFullYear(), now.getMonth(), 28).toISOString(), type:'deadline', color:'#EF4444' },
      { id:'ev3', title:'New Hire Orientation', date: new Date(now.getFullYear(), now.getMonth(), now.getDate() + 3).toISOString(), type:'onboarding', color:'#7C3AED' },
      { id:'ev4', title:'Payroll Processing', date: new Date(now.getFullYear(), now.getMonth(), 25).toISOString(), type:'payroll', color:'#10B981' },
      { id:'ev5', title:'Benefits Enrollment Deadline', date: new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString(), type:'benefits', color:'#F59E0B' },
    ]);
  };

  // ── Build overview from fetched data ──────────────────────────────────────
  const buildOverview = useCallback(() => {
    const emps = employees || [];
    const isActive     = e => /^active$/i.test(e.status) || e.status === undefined;
    const isOnLeave    = e => /on.?leave/i.test(e.status);
    const isTerminated = e => /terminated|inactive/i.test(e.status);
    const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);
    const totalPayroll = emps.reduce((s, e) => s + (parseFloat(e.salary) || 0), 0);
    const avgSalary    = emps.length ? Math.round(totalPayroll / emps.length) : 0;
    setOverview(o => ({
      ...o,
      totalEmployees:   emps.length,
      activeEmployees:  emps.filter(isActive).length,
      onLeave:          emps.filter(isOnLeave).length,
      newHires:         emps.filter(e => e.startDate && new Date(e.startDate) > thirtyDaysAgo).length,
      pendingLeave:     (leaveRequests||[]).filter(l => /pending/i.test(l.status || l.leave_status || '')).length,
      warningLetters:   (warningLetters||[]).filter(w => /active/i.test(w.status || '')).length,
      attendanceRate:   emps.length ? Math.round(emps.reduce((s, e) => s + (e.attendanceRate || 95), 0) / emps.length) : 0,
      satisfactionScore: 4.2,
      turnoverRate:     emps.length ? parseFloat(((emps.filter(isTerminated).length / emps.length) * 100).toFixed(1)) : 0,
      totalPayroll,
      avgSalary,
    }));
  }, [employees, leaveRequests, warningLetters]);

  useEffect(() => { if ((employees||[]).length) buildOverview(); }, [employees, leaveRequests, warningLetters]);

  // ── Normalise employee record ─────────────────────────────────────────────
  const normaliseEmployee = raw => {
    // Server returns snake_case: first_name, last_name, base_salary, hire_date, profile_image
    const firstName = raw.first_name  || raw.firstName  || (raw.name||raw.full_name||raw.candidate_name||'').split(' ')[0] || '';
    const lastName  = raw.last_name   || raw.lastName   || (raw.name||raw.full_name||raw.candidate_name||'').split(' ').slice(1).join(' ') || '';
    const fullName  = (firstName + ' ' + lastName).trim() || raw.name || raw.full_name || raw.candidate_name || '';
    const salary    = parseFloat(raw.base_salary || raw.salary || raw.baseSalary || 0);
    const rawPhoto  = raw.profile_image || raw.avatar || raw.photo || null;
    const photoUrl  = rawPhoto ? (rawPhoto.startsWith('http') ? rawPhoto : `https://api.jobeng.ai${rawPhoto}`) : null;
    const avatar    = photoUrl ||
      `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName||'User')}&background=0e4e96&color=fff&size=128`;
    return {
      ...raw,
      id:             String(raw.id || `e_${Math.random()}`),
      serverId:       raw.id ? String(raw.id) : null,
      employeeNumber: raw.employee_number || raw.employee_id || '',
      name:           fullName,
      firstName,      lastName,
      email:          raw.email  || raw.candidate_email || '',
      phone:          raw.phone  || raw.candidate_phone || '',
      department:     raw.department || '',
      position:       raw.position   || raw.job_title || raw.current_title || '',
      salary,
      startDate:      raw.hire_date  || raw.start_date  || raw.startDate || raw.hireDate || '',
      employeeType:   raw.employment_type || raw.employeeType || 'Full-time',
      location:       raw.location   || raw.work_location || 'Office',
      status:         raw.status     || (raw.is_active === false ? 'Inactive' : 'Active'),
      manager:        raw.manager    || raw.reporting_to || '',
      niNumber:       raw.ni_number  || raw.niNumber || '',
      taxCode:        raw.tax_code   || raw.taxCode  || '1257L',
      performanceRating: parseFloat(raw.performance_rating || raw.performanceRating || raw.rating || 3),
      attendanceRate: raw.attendanceRate || raw.attendance_rate || 95,
      leaveBalance:   raw.leaveBalance   || raw.leave_balance   || 25,
      lastReview:     raw.lastReview     || raw.last_review_date || '',
      avatar,
      skills:         Array.isArray(raw.skills) ? raw.skills :
                      typeof raw.skills === 'string' ? raw.skills.split(',').map(s => s.trim()).filter(Boolean) : [],
      warningCount:   raw.warning_count  || raw.warningCount || 0,
      notes:          raw.notes || '',
    };
  };

  // ── Computed ──────────────────────────────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    let list = [...employees];
    if (searchQ) {
      const q = searchQ.toLowerCase();
      list = list.filter(e => [e.name, e.email, e.department, e.position, ...(e.skills || [])]
        .some(v => (v || '').toLowerCase().includes(q)));
    }
    if (filters.department !== 'All') list = list.filter(e => e.department === filters.department);
    if (filters.status !== 'All')     list = list.filter(e => e.status === filters.status);
    if (filters.location !== 'All')   list = list.filter(e => e.location === filters.location);
    if (filters.employeeType !== 'All') list = list.filter(e => e.employeeType === filters.employeeType);
    list.sort((a, b) => {
      if (sortBy === 'name')       return (a.name||'').localeCompare(b.name||'');
      if (sortBy === 'salary')     return (b.salary||0) - (a.salary||0);
      if (sortBy === 'department') return (a.department||'').localeCompare(b.department||'');
      if (sortBy === 'rating')     return (b.performanceRating||0) - (a.performanceRating||0);
      if (sortBy === 'startDate')  return new Date(b.startDate||0) - new Date(a.startDate||0);
      return 0;
    });
    return list;
  }, [employees, searchQ, filters, sortBy]);

  const filteredLeave = useMemo(() => {
    let list = [...leaveRequests];
    if (leaveFilter !== 'all') {
      list = list.filter(l => (l.status || l.leave_status || 'pending').toLowerCase() === leaveFilter);
    }
    return list;
  }, [leaveRequests, leaveFilter]);

  const unreadNotifs = useMemo(() => (notifications||[]).filter(n => !n.read).length, [notifications]);

  const deptStats = useMemo(() => {
    const stats = {};
    employees.forEach(e => {
      if (!e.department) return;
      if (!stats[e.department]) stats[e.department] = { count: 0, totalSalary: 0, active: 0 };
      stats[e.department].count++;
      stats[e.department].totalSalary += parseInt(e.salary) || 0;
      if (e.status === 'Active') stats[e.department].active++;
    });
    return Object.entries(stats).sort(([, a], [, b]) => b.count - a.count);
  }, [employees]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleAddEmployee = async () => {
    if (!newEmp.name?.trim() || !newEmp.email?.trim()) {
      Alert.alert('Required', 'Name and email are required.'); return;
    }
    setSaving(true);
    try {
      // Build local employee object
      const localId = `emp_${Date.now()}`;
      const nameParts = newEmp.name.trim().split(' ');
      const skills = typeof newEmp.skills === 'string'
        ? newEmp.skills.split(',').map(s => s.trim()).filter(Boolean)
        : (Array.isArray(newEmp.skills) ? newEmp.skills : []);

      const emp = {
        ...newEmp,
        id: localId,
        name: newEmp.name.trim(),
        employeeId: `EMP${String((employees||[]).length + 1).padStart(4, '0')}`,
        salary: parseInt(newEmp.salary) || 0,
        status: 'Active', performanceRating: 3, attendanceRate: 100,
        leaveBalance: 25, warningCount: 0,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newEmp.name)}&background=0e4e96&color=fff&size=128`,
        skills,
        createdAt: new Date().toISOString(),
      };

      // Save locally first so UI updates immediately
      const localNorm = normaliseEmployee(emp);
      const updated = [localNorm, ...(employees||[])];
      setEmployees(updated);
      await AsyncStorage.setItem(`hr_employees_${companyId}`, JSON.stringify(updated));
      await AsyncStorage.setItem('hr_employees', JSON.stringify(updated));

      // Map to server snake_case schema
      const serverPayload = {
        first_name:       nameParts[0] || '',
        last_name:        nameParts.slice(1).join(' ') || '.',
        email:            newEmp.email.trim(),
        phone:            newEmp.phone || null,
        department:       newEmp.department || null,
        position:         newEmp.position || null,
        hire_date:        newEmp.startDate || new Date().toISOString().split('T')[0],
        status:           'active',
        employment_type:  (newEmp.employeeType || 'full_time').toLowerCase().replace('-', '_'),
        salary:           parseInt(newEmp.salary) || null,
        notes:            newEmp.notes || null,
        address:          newEmp.address || null,
        national_id:      newEmp.nationalId || null,
        emergency_contact_name:  newEmp.emergencyContact || null,
        emergency_contact_phone: newEmp.emergencyPhone || null,
        company_id:       companyId ? parseInt(companyId) : 1,
        manager:          newEmp.manager || null,
      };

      // POST to server (non-blocking — employee already saved locally)
      try {
        const res = await HRAPI.addEmployee(serverPayload);
        const saved = res?.data ?? res;
        const serverId = saved?.id;
        // If server assigned a real ID, update local record
        if (serverId && String(serverId) !== localId) {
          const withServerId = updated.map(e =>
            e.id === localId ? { ...e, id: serverId, serverId } : e
          );
          setEmployees(withServerId);
          await AsyncStorage.setItem(`hr_employees_${companyId}`, JSON.stringify(withServerId));
          await AsyncStorage.setItem('hr_employees', JSON.stringify(withServerId));
          // Sync to payroll with server ID
          const syncRaw = await AsyncStorage.getItem('payroll_employee_sync');
          const syncData = syncRaw ? JSON.parse(syncRaw) : {};
          syncData[serverId] = { ...emp, id: serverId, payFrequency: 'monthly', paymentDate: 28, taxCode: '1257L', source: 'hr_dashboard' };
          await AsyncStorage.setItem('payroll_employee_sync', JSON.stringify(syncData));
        }
      } catch (apiErr) {
        // Server failed but employee is already saved locally — that's OK
        console.warn('[HRDash] POST employee to server failed (saved locally):', apiErr.message);
      }

      // Sync to payroll cache with local ID
      const syncRaw2 = await AsyncStorage.getItem('payroll_employee_sync');
      const syncData2 = syncRaw2 ? JSON.parse(syncRaw2) : {};
      if (!syncData2[emp.id]) {
        syncData2[emp.id] = { ...emp, payFrequency: 'monthly', paymentDate: 28, taxCode: '1257L', source: 'hr_dashboard' };
        await AsyncStorage.setItem('payroll_employee_sync', JSON.stringify(syncData2));
      }

      setModal(null);
      setNewEmp({ name:'', email:'', phone:'', department:'Engineering', position:'', salary:'', startDate: new Date().toISOString().split('T')[0], employeeType:'Full-time', location:'Office', manager:'', address:'', nationalId:'', emergencyContact:'', emergencyPhone:'', skills:'', notes:'' });
      Alert.alert('✅ Added', `${emp.name} added successfully`);
      loadAllData(true);
    } catch (err) {
      console.error('[HRDash] handleAddEmployee error:', err);
      Alert.alert('Error', `Could not add employee: ${err.message}`);
    } finally { setSaving(false); }
  };

  const handleApproveLeave = async (approved) => {
    if (!selectedLeave) return;
    setSaving(true);
    try {
      const status = approved ? 'approved' : 'rejected';
      await HRAPI.approveLeave(selectedLeave.id, status, leaveNote);
      setLeaveRequests(prev => prev.map(l =>
        l.id === selectedLeave.id ? { ...l, status, reviewed_note: leaveNote, reviewed_at: new Date().toISOString() } : l
      ));
      if (approved) {
        setOverview(o => ({ ...o, pendingLeave: Math.max(0, o.pendingLeave - 1) }));
      }
      setModal(null); setLeaveNote(''); setSelectedLeave(null);
      Alert.alert(approved ? '✅ Approved' : '❌ Rejected', `Leave request has been ${status}`);
    } catch { Alert.alert('Error', 'Could not update leave status.'); }
    finally { setSaving(false); }
  };

  const handleProcessPayroll = async () => {
    setSaving(true);
    try {
      await HRAPI.processPayroll(companyId);
      const now = new Date().toISOString();
      setPayrollData(p => ({
        ...p,
        processedPayments: p.processedPayments + p.pendingPayments,
        pendingPayments: 0,
        lastProcessed: now,
      }));
      const totalBase = (employees||[]).filter(e => e.status === 'Active').reduce((s, e) => s + (parseInt(e.salary) || 0), 0);
      setPayrollData(p => ({
        ...p,
        totalPayroll: totalBase,
        averageSalary: (employees||[]).length ? Math.round(totalBase / (employees||[]).length) : 0,
        netPayroll: Math.round(totalBase * 0.78),
        taxes: Math.round(totalBase * 0.12),
        benefits: Math.round(totalBase * 0.08),
        deductions: Math.round(totalBase * 0.02),
      }));
      await AsyncStorage.setItem('last_payroll_process', JSON.stringify({ processedAt: now, companyId }));
      setModal(null);
      Alert.alert('✅ Processed', `Payroll processed for ${(employees||[]).filter(e => e.status === 'Active').length} active employees`);
    } catch { Alert.alert('Error', 'Could not process payroll.'); }
    finally { setSaving(false); }
  };

  const handleScheduleReview = async () => {
    if (!reviewForm.employeeId && !reviewForm.employeeName) {
      Alert.alert('Required', 'Select an employee.'); return;
    }
    setSaving(true);
    try {
      const review = {
        ...reviewForm, id: `rev_${Date.now()}`,
        company_id: companyId, status: 'Scheduled',
        created_at: new Date().toISOString(),
      };
      await HRAPI.createPerformanceReview(review);
      setPerformanceData(p => ({
        ...p, reviewsPending: p.reviewsPending + 1,
        reviews: [review, ...safeArr(p.reviews)],
      }));
      setModal(null);
      setReviewForm({ employeeId:'', employeeName:'', reviewDate: new Date().toISOString().split('T')[0], reviewer:'', period:'', type:'Annual', notes:'' });
      Alert.alert('✅ Scheduled', `Review scheduled for ${reviewForm.employeeName}`);
    } catch { Alert.alert('Error', 'Could not schedule review.'); }
    finally { setSaving(false); }
  };

  const handleIssueWarning = async () => {
    if (!warningForm.employeeName || !warningForm.description) {
      Alert.alert('Required', 'Select employee and add description.'); return;
    }
    setSaving(true);
    try {
      const warning = {
        ...warningForm, id: `warn_${Date.now()}`,
        company_id: companyId, status: 'Active',
        created_at: new Date().toISOString(),
      };
      await HRAPI.issueWarning(warning);
      setWarningLetters(prev => [warning, ...prev]);
      setEmployees(prev => prev.map(e =>
        e.id === warningForm.employeeId ? { ...e, warningCount: (e.warningCount || 0) + 1 } : e));
      setModal(null);
      setWarningForm({ employeeId:'', employeeName:'', type:'Verbal Warning', reason:'Performance', description:'', severity:'Low', issuedDate: new Date().toISOString().split('T')[0], acknowledgmentRequired: true, notes:'' });
      Alert.alert('✅ Issued', `Warning issued to ${warningForm.employeeName}`);
    } catch { Alert.alert('Error', 'Could not issue warning.'); }
    finally { setSaving(false); }
  };

  const handlePostJob = async () => {
    if (!jobForm.title || !jobForm.department) {
      Alert.alert('Required', 'Job title and department required.'); return;
    }
    setSaving(true);
    try {
      const job = { ...jobForm, id: `job_${Date.now()}`, company_id: companyId, status: 'active', created_at: new Date().toISOString() };
      await api('/api/data/jobs', 'POST', job);
      setRecruitmentData(p => ({ ...p, activeJobs: p.activeJobs + 1, jobs: [job, ...safeArr(p.jobs)] }));
      setModal(null);
      setJobForm({ title:'', department:'Engineering', location:'Office', type:'Full-time', salary:'', description:'', requirements:'' });
      Alert.alert('✅ Posted', `"${job.title}" posted successfully`);
    } catch { Alert.alert('Error', 'Could not post job.'); }
    finally { setSaving(false); }
  };

  // ── Onboarding handler ───────────────────────────────────────────────────
  const handleUpdateOnboarding = async (id, updates) => {
    const updated = (onboardingList||[]).map(o => o.id === id ? { ...o, ...updates } : o);
    setOnboardingList(updated);
    try { await HRAPI.updateOnboardingTask(id, { ...updates, company_id: companyId }); } catch {}
  };

  const handleToggleOnboardTask = async (onboardId, taskId) => {
    const ob = (onboardingList||[]).find(o => o.id === onboardId);
    if (!ob) return;
    const checklist = (ob.checklist||[]).map(t => t.id === taskId ? { ...t, done: !t.done } : t);
    const doneCount = checklist.filter(t => t.done).length;
    const progress = checklist.length > 0 ? Math.round((doneCount / checklist.length) * 100) : 0;
    const status = progress === 100 ? 'completed' : progress > 0 ? 'in_progress' : 'not_started';
    await handleUpdateOnboarding(onboardId, { checklist, progress, status });
  };

  const handleCreateOnboarding = async () => {
    if (!onboardForm.employeeName) { Alert.alert('Required', 'Select an employee.'); return; }
    setSaving(true);
    try {
      const ob = { ...onboardForm, id: `ob_${Date.now()}`, status:'not_started', progress:0, company_id: companyId,
        checklist:[
          { id:'c1', task:'IT setup & laptop', done:false },
          { id:'c2', task:'Email & accounts created', done:false },
          { id:'c3', task:'HR documentation signed', done:false },
          { id:'c4', task:'Benefits enrollment', done:false },
          { id:'c5', task:'Security training', done:false },
          { id:'c6', task:'Team introductions', done:false },
          { id:'c7', task:'Role-specific training', done:false },
          { id:'c8', task:'30-day check-in scheduled', done:false },
        ],
        createdAt: new Date().toISOString(),
      };
      await HRAPI.createOnboardingTask(ob);
      setOnboardingList(prev => [ob, ...safeArr(prev)]);
      setModal(null);
      setOnboardForm({ employeeId:'', employeeName:'', startDate:'', manager:'', department:'', checklist:[], notes:'' });
      Alert.alert('✅ Created', `Onboarding plan created for ${ob.employeeName}`);
    } catch { Alert.alert('Error', 'Could not create onboarding plan.'); }
    finally { setSaving(false); }
  };

  const handleCreateBenefit = async () => {
    if (!benefitForm.planName) { Alert.alert('Required', 'Plan name required.'); return; }
    setSaving(true);
    try {
      const plan = { ...benefitForm, id: `ben_${Date.now()}`, company_id: companyId, enrolled: 0, status:'Active', createdAt: new Date().toISOString() };
      await HRAPI.createBenefit(plan);
      setBenefitsData(p => ({ ...p, plans: [plan, ...(p.plans||[])] }));
      setModal(null);
      setBenefitForm({ planName:'', type:'Health', provider:'', cost:'', coverage:'', eligibility:'All', description:'' });
      Alert.alert('✅ Created', `Benefit plan "${plan.planName}" created`);
    } catch { Alert.alert('Error', 'Could not create benefit plan.'); }
    finally { setSaving(false); }
  };

  const handleCreatePolicy = async () => {
    if (!policyForm.title) { Alert.alert('Required', 'Policy title required.'); return; }
    setSaving(true);
    try {
      const policy = { ...policyForm, id: `pol_${Date.now()}`, company_id: companyId, version:'1.0', acknowledgedBy: 0, createdAt: new Date().toISOString() };
      await HRAPI.createPolicy(policy);
      setPolicies(prev => [policy, ...safeArr(prev)]);
      setModal(null);
      setPolicyForm({ title:'', category:'HR', description:'', effectiveDate:'', reviewDate:'', status:'Active' });
      Alert.alert('✅ Created', `Policy "${policy.title}" created`);
    } catch { Alert.alert('Error', 'Could not create policy.'); }
    finally { setSaving(false); }
  };

  const handleCreateExitInterview = async () => {
    if (!exitForm.employeeName) { Alert.alert('Required', 'Select an employee.'); return; }
    setSaving(true);
    try {
      const ei = { ...exitForm, id: `ei_${Date.now()}`, company_id: companyId, createdAt: new Date().toISOString() };
      await HRAPI.createExitInterview(ei);
      setExitInterviews(prev => [ei, ...safeArr(prev)]);
      setModal(null);
      setExitForm({ employeeId:'', employeeName:'', lastDate:'', reason:'Resignation', feedback:'', rating:'3', rehirable: true, notes:'' });
      Alert.alert('✅ Recorded', `Exit interview recorded for ${ei.employeeName}`);
    } catch { Alert.alert('Error', 'Could not record exit interview.'); }
    finally { setSaving(false); }
  };

  // ── Bulk action handler ───────────────────────────────────────────────────
  const handleBulkAction = async (action, value) => {
    if (selectedBulk.length === 0) { Alert.alert('None selected', 'Select employees first.'); return; }
    Alert.alert(`Bulk ${action}`, `Apply "${action}" to ${selectedBulk.length} employees?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Apply', onPress: async () => {
        const updatedEmps = (employees||[]).map(e =>
          selectedBulk.includes(e.id) ? { ...e, ...(action === 'status' ? { status: value } : action === 'department' ? { department: value } : action === 'location' ? { location: value } : {}) } : e
        );
        setEmployees(updatedEmps);
        await AsyncStorage.setItem(`hr_employees_${companyId}`, JSON.stringify(updatedEmps));
        setSelectedBulk([]);
        setBulkMode(false);
        setShowBulkModal(false);
        Alert.alert('✅ Done', `Updated ${selectedBulk.length} employees`);
      }}
    ]);
  };

  const exportReport = async () => {
    const lines = [
      `HR Dashboard Report — ${fmtDate(new Date().toISOString())}`,
      `Company: ${companyName}`,
      '',
      `WORKFORCE`,
      `  Total Employees: ${overview.totalEmployees}`,
      `  Active: ${overview.activeEmployees}`,
      `  On Leave: ${overview.onLeave}`,
      `  New Hires (30d): ${overview.newHires}`,
      `  Turnover Rate: ${overview.turnoverRate}%`,
      `  Attendance Rate: ${overview.attendanceRate}%`,
      '',
      `PAYROLL`,
      `  Total Payroll: ${fmtMoney(payrollData.totalPayroll)}`,
      `  Average Salary: ${fmtMoney(payrollData.averageSalary)}`,
      `  Net Payroll: ${fmtMoney(payrollData.netPayroll)}`,
      '',
      `PERFORMANCE`,
      `  Reviews Completed: ${performanceData.reviewsCompleted}`,
      `  Average Rating: ${performanceData.averageRating}/5.0`,
      `  Top Performers: ${performanceData.topPerformers}`,
      '',
      `LEAVE`,
      `  Pending Requests: ${overview.pendingLeave}`,
      `  Total Requests: ${(leaveRequests||[]).length}`,
      '',
      `RECRUITMENT`,
      `  Active Jobs: ${recruitmentData.activeJobs}`,
      `  Applications: ${recruitmentData.applications}`,
      `  Hired: ${recruitmentData.hired}`,
      '',
      `TRAINING`,
      `  Compliance Rate: ${trainingData.complianceRate}%`,
      `  Modules Completed: ${trainingData.completed}/${trainingData.totalModules}`,
    ];
    await Share.share({ message: lines.join('\n'), title: 'HR Dashboard Report' });
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  // ── Reusable render helpers ───────────────────────────────────────────────
  const StatCard = ({ title, value, icon, color, subtitle, trend, onPress, wide }) => (
    <TouchableOpacity style={[sty.statCard, { borderLeftColor: color, width: wide ? width - 40 : (width - 52) / 2 }]}
      onPress={onPress} disabled={!onPress} activeOpacity={0.85}>
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Text style={sty.statValue}>{value}</Text>
          {trend !== undefined && trend !== null && (
            <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: trend >= 0 ? '#10b98120' : '#dc262620', borderRadius: 10, paddingHorizontal: 6, paddingVertical: 2 }}>
              <MaterialCommunityIcons name={trend >= 0 ? 'trending-up' : 'trending-down'} size={12} color={trend >= 0 ? '#10b981' : '#dc2626'} />
              <Text style={{ fontSize: 10, fontWeight: '700', color: trend >= 0 ? '#10b981' : '#dc2626', marginLeft: 2 }}>{Math.abs(trend)}%</Text>
            </View>
          )}
        </View>
        <Text style={sty.statTitle}>{title}</Text>
        {subtitle && <Text style={sty.statSubtitle}>{subtitle}</Text>}
      </View>
      <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: color + '18', alignItems: 'center', justifyContent: 'center' }}>
        <MaterialCommunityIcons name={icon} size={22} color={color} />
      </View>
    </TouchableOpacity>
  );

  const SectionCard = ({ title, children, action, onAction }) => (
    <View style={sty.sectionCard}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <Text style={sty.sectionTitle}>{title}</Text>
        {action && <TouchableOpacity onPress={onAction}><Text style={sty.seeAll}>{action}</Text></TouchableOpacity>}
      </View>
      {children}
    </View>
  );

  const BarChart = ({ data, valueKey, labelKey, color = '#0e4e96', max }) => {
    const maxVal = max || Math.max(...safeArr(data).map(d => d[valueKey] || 0), 1);
    return (
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 6, height: 100 }}>
        {safeArr(data).map((item, i) => {
          const val = item[valueKey] || 0;
          const h = Math.max((val / maxVal) * 80, 4);
          const deptColor = DEPT_COLORS[item[labelKey]] || color;
          return (
            <View key={i} style={{ flex: 1, alignItems: 'center' }}>
              <Text style={{ fontSize: 9, fontWeight: '700', color: deptColor, marginBottom: 3 }}>{typeof val === 'number' && val > 999 ? fmtMoney(val) : val}{typeof val === 'number' && val <= 100 && valueKey !== 'count' && valueKey !== 'employees' ? '%' : ''}</Text>
              <View style={{ width: '100%', height: h, backgroundColor: deptColor, borderRadius: 3 }} />
              <Text style={{ fontSize: 9, color: '#6B7280', marginTop: 4, textAlign: 'center' }} numberOfLines={1}>{item[labelKey]}</Text>
            </View>
          );
        })}
      </View>
    );
  };

  const LeaveTypeRow = ({ icon, label, count, color, total }) => (
    <View style={{ marginBottom: 10 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
        <MaterialCommunityIcons name={icon} size={16} color={color} style={{ marginRight: 8 }} />
        <Text style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: '600' }}>{label}</Text>
        <Text style={{ fontSize: 13, fontWeight: '800', color }}>{count}</Text>
        {total ? <Text style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 4 }}>/ {total}</Text> : null}
      </View>
      {total ? <ProgressBar value={count} max={total} color={color} height={5} /> : null}
    </View>
  );


  // ── Tab: ONBOARDING ──────────────────────────────────────────────────────
  const renderOnboardingTab = () => {
    const list = safeArr(onboardingList);
    const filtered = onboardFilter === 'all' ? list : list.filter(o => o.status === onboardFilter);
    const completed = list.filter(o => o.status === 'completed').length;
    const inProgress = list.filter(o => o.status === 'in_progress').length;
    const notStarted = list.filter(o => o.status === 'not_started').length;
    return (
      <View style={{ flex:1 }}>
        <View style={{ backgroundColor:'#fff', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#E5E7EB' }}>
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:10 }}>
            <StatCard title="Total" value={list.length} icon="account-check" color="#7C3AED" subtitle="Onboarding records" />
            <StatCard title="In Progress" value={inProgress} icon="progress-check" color="#3B82F6" subtitle="Active" />
            <StatCard title="Completed" value={completed} icon="check-all" color="#10B981" subtitle="Done" trend={8} />
            <StatCard title="Not Started" value={notStarted} icon="clock-outline" color="#9CA3AF" subtitle="Pending" />
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6, marginBottom:4 }}>
            {[['all','All'],['not_started','Not Started'],['in_progress','In Progress'],['completed','Completed']].map(([k,l])=>(
              <TouchableOpacity key={k} onPress={()=>setOnboardFilter(k)}
                style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:onboardFilter===k?'#7C3AED':'#F3F4F6', borderWidth:1, borderColor:onboardFilter===k?'#7C3AED':'#E5E7EB' }}>
                <Text style={{ fontSize:12, fontWeight:'600', color:onboardFilter===k?'#fff':'#6B7280' }}>{l}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={()=> can('create_onboarding') ? setModal('create_onboard') : Alert.alert('Restricted', 'Onboarding creation is restricted.')}
              style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:'#7C3AED', flexDirection:'row', alignItems:'center', gap:4 }}>
              <MaterialCommunityIcons name="plus" size={14} color="#fff"/>
              <Text style={{ fontSize:12, fontWeight:'700', color:'#fff' }}>New Plan</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
        <FlatList data={filtered} keyExtractor={(o,i)=>String(o.id||i)}
          contentContainerStyle={{ padding:14, paddingBottom:80 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#7C3AED']}/>}
          ListEmptyComponent={<View style={{ alignItems:'center', paddingVertical:60 }}><MaterialCommunityIcons name="account-check-outline" size={56} color="#E5E7EB"/><Text style={{ color:'#9CA3AF', marginTop:12 }}>No onboarding plans</Text></View>}
          renderItem={({item:ob})=>{
            const doneCount = safeArr(ob.checklist).filter(t=>t.done).length;
            const totalTasks = safeArr(ob.checklist).length;
            const prog = totalTasks > 0 ? pct(doneCount, totalTasks) : ob.progress || 0;
            const sCfg = { completed:{color:'#10B981',bg:'#ECFDF5',label:'Completed'}, in_progress:{color:'#3B82F6',bg:'#EFF6FF',label:'In Progress'}, not_started:{color:'#9CA3AF',bg:'#F9FAFB',label:'Not Started'} }[ob.status]||{color:'#6B7280',bg:'#F9FAFB',label:ob.status||'Unknown'};
            return (
              <View style={[sty.empCard, { marginBottom:12 }]}>
                <View style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                  <Avatar name={ob.employeeName||'?'} size={48}/>
                  <View style={{ flex:1 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:3 }}>
                      <Text style={{ fontSize:15, fontWeight:'800', color:'#111827', flex:1 }}>{ob.employeeName||'—'}</Text>
                      <View style={{ backgroundColor:sCfg.bg, borderRadius:10, paddingHorizontal:8, paddingVertical:3 }}>
                        <Text style={{ fontSize:9, fontWeight:'700', color:sCfg.color }}>{sCfg.label}</Text>
                      </View>
                    </View>
                    <Text style={{ fontSize:12, color:'#6B7280' }}>{ob.department} · Manager: {ob.manager||'—'}</Text>
                    <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>Start: {fmtDate(ob.startDate)}</Text>
                  </View>
                </View>
                {/* Progress */}
                <View style={{ marginBottom:12 }}>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:5 }}>
                    <Text style={{ fontSize:12, color:'#374151', fontWeight:'600' }}>Onboarding Progress</Text>
                    <Text style={{ fontSize:12, fontWeight:'900', color:sCfg.color }}>{prog}% ({doneCount}/{totalTasks} tasks)</Text>
                  </View>
                  <ProgressBar value={prog} max={100} color={sCfg.color} height={8}/>
                </View>
                {/* Checklist */}
                <View style={{ backgroundColor:'#F9FAFB', borderRadius:12, padding:10, gap:6 }}>
                  {safeArr(ob.checklist).map((task,ti)=>(
                    <TouchableOpacity key={task.id||ti} style={{ flexDirection:'row', alignItems:'center', gap:10 }}
                      onPress={()=>handleToggleOnboardTask(ob.id, task.id)}>
                      <View style={{ width:22, height:22, borderRadius:11, borderWidth:2,
                        borderColor:task.done?'#10B981':'#D1D5DB', backgroundColor:task.done?'#10B981':'transparent',
                        alignItems:'center', justifyContent:'center' }}>
                        {task.done && <MaterialCommunityIcons name="check" size={14} color="#fff"/>}
                      </View>
                      <Text style={{ flex:1, fontSize:12, color:task.done?'#6B7280':'#374151', textDecorationLine:task.done?'line-through':'none', fontWeight:task.done?'400':'600' }}>{task.task}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {ob.status !== 'completed' && prog === 100 && (
                  <TouchableOpacity style={{ backgroundColor:'#10B981', borderRadius:10, padding:10, flexDirection:'row', justifyContent:'center', gap:6, alignItems:'center', marginTop:10 }}
                    onPress={()=>handleUpdateOnboarding(ob.id,{status:'completed'})}>
                    <MaterialCommunityIcons name="check-all" size={16} color="#fff"/>
                    <Text style={{ color:'#fff', fontWeight:'700', fontSize:12 }}>Mark Onboarding Complete</Text>
                  </TouchableOpacity>
                )}
              </View>
            );
          }}
        />
      </View>
    );
  };

  // ── Tab: DISCIPLINARY (Warnings + Exit Interviews) ─────────────────────────
  const renderDisciplinaryTab = () => {
    const warnings = safeArr(warningLetters);
    const exits = safeArr(exitInterviews);
    const allItems = discFilter === 'warnings' ? warnings : discFilter === 'exits' ? exits : [...warnings, ...exits.map(e=>({...e,_isExit:true}))];
    const activeWarnings = warnings.filter(w=>w.status==='Active').length;
    const criticalWarnings = warnings.filter(w=>w.severity==='Critical'||w.severity==='High').length;
    const sevColor = { Low:'#10B981', Medium:'#F59E0B', High:'#F97316', Critical:'#7C3AED' };
    return (
      <View style={{ flex:1 }}>
        <View style={{ backgroundColor:'#fff', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#E5E7EB' }}>
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:10 }}>
            <StatCard title="Total Warnings" value={warnings.length} icon="alert-circle" color="#B91C1C" subtitle="All issued" />
            <StatCard title="Active" value={activeWarnings} icon="alert" color="#EF4444" subtitle="Unresolved" />
            <StatCard title="High/Critical" value={criticalWarnings} icon="alert-octagon" color="#7C3AED" subtitle="Severe" />
            <StatCard title="Exit Interviews" value={exits.length} icon="account-arrow-right" color="#374151" subtitle="Recorded" />
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6, marginBottom:4 }}>
            {[['all','All'],['warnings','Warnings'],['exits','Exit Interviews']].map(([k,l])=>(
              <TouchableOpacity key={k} onPress={()=>setDiscFilter(k)}
                style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:discFilter===k?'#B91C1C':'#F3F4F6', borderWidth:1, borderColor:discFilter===k?'#B91C1C':'#E5E7EB' }}>
                <Text style={{ fontSize:12, fontWeight:'600', color:discFilter===k?'#fff':'#6B7280' }}>{l}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={()=> can('issue_warning') ? setModal('issue_warning') : Alert.alert('Restricted','Warning issuance is restricted.')}
              style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:'#EF4444', flexDirection:'row', alignItems:'center', gap:4 }}>
              <MaterialCommunityIcons name="alert-plus" size={14} color="#fff"/>
              <Text style={{ fontSize:12, fontWeight:'700', color:'#fff' }}>Issue Warning</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={()=> can('create_exit_interview') ? setModal('create_exit') : Alert.alert('Restricted', 'Exit interview creation is restricted.')}
              style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:'#374151', flexDirection:'row', alignItems:'center', gap:4 }}>
              <MaterialCommunityIcons name="plus" size={14} color="#fff"/>
              <Text style={{ fontSize:12, fontWeight:'700', color:'#fff' }}>Exit Interview</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
        <FlatList data={allItems} keyExtractor={(item,i)=>String(item.id||i)}
          contentContainerStyle={{ padding:14, paddingBottom:80 }}
          ListEmptyComponent={<View style={{ alignItems:'center', paddingVertical:60 }}><MaterialCommunityIcons name="shield-check" size={56} color="#E5E7EB"/><Text style={{ color:'#9CA3AF', marginTop:12 }}>No records found</Text></View>}
          renderItem={({item:w})=>{
            if (w._isExit) {
              const ratingColor = parseInt(w.rating)>=4?'#10B981':parseInt(w.rating)>=3?'#F59E0B':'#EF4444';
              return (
                <View style={[sty.leaveCard, { borderLeftWidth:4, borderLeftColor:'#374151' }]}>
                  <View style={{ flexDirection:'row', alignItems:'flex-start', gap:10 }}>
                    <Avatar name={w.employeeName||'?'} size={44}/>
                    <View style={{ flex:1 }}>
                      <View style={{ flexDirection:'row', alignItems:'center', gap:8 }}>
                        <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', flex:1 }}>{w.employeeName||'—'}</Text>
                        <View style={{ backgroundColor:'#F1F5F9', borderRadius:10, paddingHorizontal:8, paddingVertical:3 }}>
                          <Text style={{ fontSize:9, fontWeight:'700', color:'#374151' }}>EXIT INTERVIEW</Text>
                        </View>
                      </View>
                      <Text style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>Reason: {w.reason||'—'} · Last day: {fmtDate(w.lastDate)}</Text>
                      <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginTop:5 }}>
                        <View style={{ backgroundColor:ratingColor+'20', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                          <Text style={{ fontSize:10, fontWeight:'700', color:ratingColor }}>Satisfaction: {w.rating}/5</Text>
                        </View>
                        <View style={{ backgroundColor:w.rehirable?'#ECFDF5':'#FEF2F2', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                          <Text style={{ fontSize:10, fontWeight:'700', color:w.rehirable?'#059669':'#EF4444' }}>{w.rehirable?'Rehirable':'Not Rehirable'}</Text>
                        </View>
                      </View>
                      {w.feedback && <Text style={{ fontSize:12, color:'#6B7280', marginTop:6, lineHeight:17 }} numberOfLines={3}>{w.feedback}</Text>}
                    </View>
                  </View>
                </View>
              );
            }
            const sc = sevColor[w.severity]||'#F59E0B';
            return (
              <View style={[sty.leaveCard, { borderLeftWidth:4, borderLeftColor:sc }]}>
                <View style={{ flexDirection:'row', alignItems:'flex-start', gap:10 }}>
                  <Avatar name={w.employeeName||'?'} size={44}/>
                  <View style={{ flex:1 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:4 }}>
                      <Text style={{ fontSize:14, fontWeight:'800', color:'#111827', flex:1 }}>{w.employeeName||'—'}</Text>
                      <View style={{ backgroundColor:sc+'20', borderRadius:10, paddingHorizontal:8, paddingVertical:3 }}>
                        <Text style={{ fontSize:9, fontWeight:'700', color:sc }}>{w.severity}</Text>
                      </View>
                    </View>
                    <Text style={{ fontSize:12, color:'#6B7280' }}>{w.type} · {w.reason}</Text>
                    <Text style={{ fontSize:12, color:'#374151', marginTop:5, lineHeight:17 }} numberOfLines={3}>{w.description}</Text>
                    <View style={{ flexDirection:'row', gap:8, marginTop:6 }}>
                      <View style={{ backgroundColor:w.status==='Active'?'#FEF2F2':'#ECFDF5', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                        <Text style={{ fontSize:10, fontWeight:'700', color:w.status==='Active'?'#EF4444':'#10B981' }}>{w.status||'Active'}</Text>
                      </View>
                      <Text style={{ fontSize:11, color:'#9CA3AF', alignSelf:'center' }}>{fmtDate(w.issuedDate||w.created_at)}</Text>
                    </View>
                  </View>
                </View>
                {(w.status==='Active'||!w.status) && (
                  <View style={{ flexDirection:'row', gap:8, marginTop:10 }}>
                    <TouchableOpacity style={{ flex:1, backgroundColor:'#ECFDF5', borderRadius:10, padding:9, flexDirection:'row', justifyContent:'center', gap:5, alignItems:'center' }}
                      onPress={()=>can('resolve_warnings') ? setWarningLetters(prev=>prev.map(x=>x.id===w.id?{...x,status:'Resolved'}:x)) : Alert.alert('Restricted','Resolving warnings is restricted.')}>
                      <MaterialCommunityIcons name="check" size={14} color="#10B981"/>
                      <Text style={{ fontSize:12, fontWeight:'700', color:'#10B981' }}>Resolve</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={{ flex:1, backgroundColor:'#FEF2F2', borderRadius:10, padding:9, flexDirection:'row', justifyContent:'center', gap:5, alignItems:'center' }}
                      onPress={()=>can('delete_warnings') ? setWarningLetters(prev=>prev.filter(x=>x.id!==w.id)) : Alert.alert('Restricted','Deleting warnings is restricted.')}>
                      <MaterialCommunityIcons name="delete" size={14} color="#EF4444"/>
                      <Text style={{ fontSize:12, fontWeight:'700', color:'#EF4444' }}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            );
          }}
        />
      </View>
    );
  };

  // ── Tab: COMPLIANCE ───────────────────────────────────────────────────────
  const renderComplianceTab = () => {
    const pols = safeArr(policies);
    const filtered = complianceFilter === 'all' ? pols : pols.filter(p => p.category === complianceFilter);
    const cats = [...new Set(pols.map(p=>p.category))];
    const avgAck = pols.length && (employees||[]).length
      ? Math.round((pols||[]).reduce((s,p)=>s+(p.acknowledgedBy||0),0) / ((pols||[]).length * ((employees||[]).length||1)) * 100) : 0;
    return (
      <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0891B2']}/>}>

        {/* Compliance health */}
        <View style={{ backgroundColor:'#fff', borderRadius:16, padding:18, marginBottom:14, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, elevation:2 }}>
          <View style={{ flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
            <Text style={{ fontSize:16, fontWeight:'800', color:'#111827' }}>Compliance Health</Text>
            <View style={{ backgroundColor: overview.complianceScore>=90?'#ECFDF5':overview.complianceScore>=75?'#FFFBEB':'#FEF2F2', borderRadius:10, paddingHorizontal:10, paddingVertical:4 }}>
              <Text style={{ fontSize:12, fontWeight:'900', color:overview.complianceScore>=90?'#059669':overview.complianceScore>=75?'#D97706':'#EF4444' }}>{overview.complianceScore||97}%</Text>
            </View>
          </View>
          <ProgressBar value={overview.complianceScore||97} max={100} color={overview.complianceScore>=90?'#10B981':overview.complianceScore>=75?'#F59E0B':'#EF4444'} height={10}/>
          <View style={{ flexDirection:'row', justifyContent:'space-between', marginTop:12 }}>
            {[
              { label:'Policies Active', val: pols.filter(p=>p.status==='Active').length, icon:'file-document-check' },
              { label:'Avg Acknowledgment', val: `${avgAck}%`, icon:'account-check' },
              { label:'Due for Review', val: pols.filter(p=>p.reviewDate && new Date(p.reviewDate) < new Date()).length, icon:'clock-alert' },
            ].map((m,i)=>(
              <View key={i} style={{ flex:1, alignItems:'center' }}>
                <MaterialCommunityIcons name={m.icon} size={20} color='#0891B2'/>
                <Text style={{ fontSize:16, fontWeight:'900', color:'#111827', marginTop:4 }}>{m.val}</Text>
                <Text style={{ fontSize:10, color:'#9CA3AF', textAlign:'center', marginTop:2 }}>{m.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* Category filter */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6, marginBottom:14 }}>
          {['all', ...cats].map(cat=>(
            <TouchableOpacity key={cat} onPress={()=>setComplianceFilter(cat)}
              style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:complianceFilter===cat?'#0891B2':'#F3F4F6', borderWidth:1, borderColor:complianceFilter===cat?'#0891B2':'#E5E7EB' }}>
              <Text style={{ fontSize:12, fontWeight:'600', color:complianceFilter===cat?'#fff':'#6B7280' }}>{cat === 'all' ? 'All Categories' : cat}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Policies */}
        <SectionCard title={`Policies & Documents (${filtered.length})`} action="+ New Policy" onAction={()=>can('create_policy') ? setModal('create_policy') : Alert.alert('Restricted','Policy creation is restricted.')}>
          {filtered.map((pol,i)=>{
            const ackPct = (employees||[]).length ? pct(pol.acknowledgedBy||0, (employees||[]).length) : 0;
            const overdue = pol.reviewDate && new Date(pol.reviewDate) < new Date();
            return (
              <View key={pol.id||i} style={{ paddingVertical:12, borderBottomWidth:i<filtered.length-1?1:0, borderBottomColor:'#F8FAFC' }}>
                <View style={{ flexDirection:'row', alignItems:'flex-start', gap:10 }}>
                  <View style={{ width:38, height:38, borderRadius:10, backgroundColor:'#E0F2FE', alignItems:'center', justifyContent:'center' }}>
                    <MaterialCommunityIcons name="file-document" size={20} color="#0891B2"/>
                  </View>
                  <View style={{ flex:1 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:6, marginBottom:3 }}>
                      <Text style={{ fontSize:13, fontWeight:'800', color:'#111827', flex:1 }}>{pol.title}</Text>
                      {overdue && <View style={{ backgroundColor:'#FEF2F2', borderRadius:8, paddingHorizontal:7, paddingVertical:2 }}>
                        <Text style={{ fontSize:9, fontWeight:'700', color:'#EF4444' }}>REVIEW DUE</Text>
                      </View>}
                    </View>
                    <View style={{ flexDirection:'row', gap:6, marginBottom:6 }}>
                      <Pill label={pol.category} color="#0891B2" small/>
                      <Pill label={`v${pol.version||'1.0'}`} color="#6B7280" small/>
                      <Pill label={pol.status||'Active'} color={pol.status==='Active'?'#10B981':'#9CA3AF'} small/>
                    </View>
                    <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                      <Text style={{ fontSize:11, color:'#6B7280' }}>Acknowledged by {pol.acknowledgedBy||0}/{(employees||[]).length}</Text>
                      <Text style={{ fontSize:11, fontWeight:'700', color:ackPct>=90?'#10B981':ackPct>=75?'#F59E0B':'#EF4444' }}>{ackPct}%</Text>
                    </View>
                    <ProgressBar value={pol.acknowledgedBy||0} max={(employees||[]).length||1} color={ackPct>=90?'#10B981':ackPct>=75?'#F59E0B':'#EF4444'} height={5}/>
                    <Text style={{ fontSize:10, color:'#9CA3AF', marginTop:4 }}>Effective: {fmtDate(pol.effectiveDate)} · Review: {fmtDate(pol.reviewDate)}</Text>
                  </View>
                </View>
              </View>
            );
          })}
          {filtered.length === 0 && (
            <View style={{ alignItems:'center', paddingVertical:32 }}>
              <MaterialCommunityIcons name="file-document-outline" size={40} color="#E5E7EB"/>
              <Text style={{ color:'#9CA3AF', marginTop:8 }}>No policies found</Text>
            </View>
          )}
        </SectionCard>

        {/* Compliance calendar */}
        <SectionCard title="Upcoming Compliance Events">
          {safeArr(calEvents).map((ev,i)=>(
            <View key={ev.id||i} style={{ flexDirection:'row', alignItems:'center', paddingVertical:9, borderBottomWidth:i<safeArr(calEvents).length-1?1:0, borderBottomColor:'#F8FAFC', gap:10 }}>
              <View style={{ width:36, height:36, borderRadius:10, backgroundColor:(ev.color||'#0891B2')+'15', alignItems:'center', justifyContent:'center' }}>
                <MaterialCommunityIcons name={ev.type==='payroll'?'cash-multiple':ev.type==='deadline'?'clock-alert':ev.type==='onboarding'?'account-plus':'calendar'} size={18} color={ev.color||'#0891B2'}/>
              </View>
              <View style={{ flex:1 }}>
                <Text style={{ fontSize:13, fontWeight:'700', color:'#111827' }}>{ev.title}</Text>
                <Text style={{ fontSize:11, color:'#6B7280', marginTop:2 }}>{fmtDate(ev.date)}</Text>
              </View>
              {new Date(ev.date) > new Date() && new Date(ev.date) < new Date(Date.now()+7*86400000) && (
                <View style={{ backgroundColor:'#FEF9C3', borderRadius:8, paddingHorizontal:8, paddingVertical:3 }}>
                  <Text style={{ fontSize:9, fontWeight:'700', color:'#D97706' }}>SOON</Text>
                </View>
              )}
            </View>
          ))}
        </SectionCard>
      </ScrollView>
    );
  };

  // ── Tab: REPORTS ──────────────────────────────────────────────────────────
  const renderReportsTab = () => {
    const reportTypes = [
      { id:'overview',     label:'HR Overview',         icon:'view-dashboard',  color:'#0e4e96' },
      { id:'employees',    label:'Employee Report',     icon:'account-group',   color:'#10b981' },
      { id:'payroll',      label:'Payroll Report',      icon:'cash-multiple',   color:'#059669' },
      { id:'performance',  label:'Performance Report',  icon:'chart-line',      color:'#8b5cf6' },
      { id:'leave',        label:'Leave Report',        icon:'calendar-clock',  color:'#f59e0b' },
      { id:'recruitment',  label:'Recruitment Report',  icon:'account-plus',    color:'#06b6d4' },
      { id:'training',     label:'Training Report',     icon:'school',          color:'#ec4899' },
      { id:'compliance',   label:'Compliance Report',   icon:'shield-check',    color:'#0891B2' },
      { id:'diversity',    label:'Diversity Report',    icon:'account-multiple', color:'#7C3AED' },
      { id:'turnover',     label:'Turnover Analysis',   icon:'trending-down',   color:'#EF4444' },
    ];
    const selectedReport = reportTypes.find(r=>r.id===reportFilter)||reportTypes[0];
    return (
      <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#374151']}/>}>

        {/* Report type selector */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:8, marginBottom:16 }}>
          {reportTypes.map(r=>(
            <TouchableOpacity key={r.id} onPress={()=>setReportFilter(r.id)}
              style={{ paddingHorizontal:14, paddingVertical:9, borderRadius:14, backgroundColor:reportFilter===r.id?r.color:'#fff',
                borderWidth:1, borderColor:reportFilter===r.id?r.color:'#E5E7EB',
                flexDirection:'row', alignItems:'center', gap:6,
                shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:1 }}>
              <MaterialCommunityIcons name={r.icon} size={14} color={reportFilter===r.id?'#fff':r.color}/>
              <Text style={{ fontSize:12, fontWeight:'600', color:reportFilter===r.id?'#fff':'#374151' }}>{r.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* Dynamic report content */}
        <View style={{ backgroundColor:'#fff', borderRadius:16, padding:18, marginBottom:14, shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, elevation:2 }}>
          <View style={{ flexDirection:'row', alignItems:'center', gap:10, marginBottom:16 }}>
            <View style={{ width:44, height:44, borderRadius:12, backgroundColor:selectedReport.color+'15', alignItems:'center', justifyContent:'center' }}>
              <MaterialCommunityIcons name={selectedReport.icon} size={24} color={selectedReport.color}/>
            </View>
            <View style={{ flex:1 }}>
              <Text style={{ fontSize:16, fontWeight:'900', color:'#111827' }}>{selectedReport.label}</Text>
              <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:2 }}>Generated {new Date().toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</Text>
            </View>
          </View>

          {reportFilter === 'overview' && (
            <View>
              {[['Total Employees',overview.totalEmployees,'account-group','#0e4e96'],['Active',overview.activeEmployees,'account-check','#10b981'],['Attendance Rate',`${overview.attendanceRate}%`,'calendar-check','#06b6d4'],['Satisfaction',`${overview.satisfactionScore}/5`,'heart','#ec4899'],['Turnover Rate',`${overview.turnoverRate}%`,'trending-down','#EF4444'],['Avg Tenure',`${overview.avgTenure||3.2} yrs`,'clock','#F59E0B']].map(([l,v,icon,color],i)=>(
                <View key={l} style={{ flexDirection:'row', alignItems:'center', paddingVertical:10, borderBottomWidth:i<5?1:0, borderBottomColor:'#F8FAFC', gap:10 }}>
                  <View style={{ width:34, height:34, borderRadius:17, backgroundColor:color+'15', alignItems:'center', justifyContent:'center' }}>
                    <MaterialCommunityIcons name={icon} size={16} color={color}/>
                  </View>
                  <Text style={{ flex:1, fontSize:13, color:'#374151', fontWeight:'600' }}>{l}</Text>
                  <Text style={{ fontSize:15, fontWeight:'900', color:color }}>{v}</Text>
                </View>
              ))}
            </View>
          )}
          {reportFilter === 'payroll' && (
            <View>
              {[['Total Payroll',fmtMoney(payrollData.totalPayroll),'#10b981'],['Average Salary',fmtMoney(payrollData.averageSalary),'#0e4e96'],['Net Payroll',fmtMoney(payrollData.netPayroll),'#8b5cf6'],['Total Taxes',fmtMoney(payrollData.taxes),'#EF4444'],['Benefits Cost',fmtMoney(payrollData.benefits),'#F59E0B'],['Bonuses',fmtMoney(payrollData.bonuses),'#06b6d4']].map(([l,v,color],i)=>(
                <View key={l} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<5?1:0, borderBottomColor:'#F8FAFC' }}>
                  <Text style={{ fontSize:13, color:'#374151', fontWeight:'600' }}>{l}</Text>
                  <Text style={{ fontSize:14, fontWeight:'900', color }}>{v}</Text>
                </View>
              ))}
            </View>
          )}
          {reportFilter === 'employees' && (
            <View>
              {(deptStats||[]).map(([dept,stats],i)=>(
                <View key={dept} style={{ paddingVertical:10, borderBottomWidth:i<(deptStats||[]).length-1?1:0, borderBottomColor:'#F8FAFC' }}>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                    <Text style={{ fontSize:13, color:'#374151', fontWeight:'700' }}>{dept}</Text>
                    <Text style={{ fontSize:12, color:DEPT_COLORS[dept]||'#0e4e96', fontWeight:'800' }}>{stats.count} emp · {fmtMoney(stats.totalSalary)}</Text>
                  </View>
                  <ProgressBar value={stats.count} max={(employees||[]).length||1} color={DEPT_COLORS[dept]||'#0e4e96'} height={5}/>
                </View>
              ))}
            </View>
          )}
          {reportFilter === 'diversity' && (
            <View>
              <Text style={{ fontSize:13, color:'#6B7280', lineHeight:20, marginBottom:12 }}>Diversity metrics based on current workforce composition.</Text>
              {[
                { label:'Location Diversity', data: safeArr([...new Set((employees||[]).map(e=>e.location))]).map(l=>({ name:l, count:(employees||[]).filter(e=>e.location===l).length })) },
                { label:'Employment Type', data: safeArr([...new Set((employees||[]).map(e=>e.employeeType))]).map(t=>({ name:t, count:(employees||[]).filter(e=>e.employeeType===t).length })) },
              ].map(section=>(
                <View key={section.label} style={{ marginBottom:16 }}>
                  <Text style={{ fontSize:12, fontWeight:'800', color:'#374151', marginBottom:8 }}>{section.label}</Text>
                  {section.data.map((item,j)=>(
                    <View key={j} style={{ flexDirection:'row', alignItems:'center', marginBottom:6 }}>
                      <Text style={{ width:90, fontSize:11, color:'#6B7280' }}>{item.name}</Text>
                      <View style={{ flex:1, height:6, backgroundColor:'#F1F5F9', borderRadius:3, overflow:'hidden', marginHorizontal:8 }}>
                        <View style={{ width:`${pct(item.count, (employees||[]).length||1)}%`, height:'100%', backgroundColor:'#7C3AED', borderRadius:3 }}/>
                      </View>
                      <Text style={{ fontSize:11, fontWeight:'700', color:'#7C3AED', width:28, textAlign:'right' }}>{item.count}</Text>
                    </View>
                  ))}
                </View>
              ))}
            </View>
          )}
          {reportFilter === 'turnover' && (
            <View>
              {[['Current Turnover Rate',`${overview.turnoverRate}%`,'#EF4444'],['Industry Benchmark','8-12%','#6B7280'],['YTD Separations',(employees||[]).filter(e=>e.status==='Terminated').length,'#F97316'],['New Hires (30d)',overview.newHires,'#10B981'],['Net Change',overview.newHires-(employees||[]).filter(e=>e.status==='Terminated').length,'#0e4e96'],['Avg Tenure',`${overview.avgTenure||3.2} years`,'#8B5CF6']].map(([l,v,color],i)=>(
                <View key={l} style={{ flexDirection:'row', justifyContent:'space-between', paddingVertical:10, borderBottomWidth:i<5?1:0, borderBottomColor:'#F8FAFC' }}>
                  <Text style={{ fontSize:13, color:'#374151', fontWeight:'600' }}>{l}</Text>
                  <Text style={{ fontSize:14, fontWeight:'900', color }}>{v}</Text>
                </View>
              ))}
              <View style={{ marginTop:16, padding:12, backgroundColor:'#FEF2F2', borderRadius:12 }}>
                <Text style={{ fontSize:12, fontWeight:'700', color:'#EF4444', marginBottom:4 }}>⚠ Top Turnover Risk Indicators</Text>
                {['Low performance scores without action plans','High warning letter frequency in department','Extended pending leave requests','Salary significantly below market average'].map((r,i)=>(
                  <Text key={i} style={{ fontSize:11, color:'#374151', marginTop:3 }}>• {r}</Text>
                ))}
              </View>
            </View>
          )}
          {!['overview','payroll','employees','diversity','turnover'].includes(reportFilter) && (
            <Text style={{ color:'#9CA3AF', textAlign:'center', paddingVertical:20, fontSize:13 }}>Detailed {selectedReport.label} data displayed here based on live records.</Text>
          )}
        </View>

        {/* Export buttons */}
        <View style={{ gap:10, marginBottom:80 }}>
          <TouchableOpacity style={{ backgroundColor:'#0e4e96', borderRadius:14, padding:15, flexDirection:'row', justifyContent:'center', gap:8, alignItems:'center' }} onPress={exportReport}>
            <MaterialCommunityIcons name="share-variant" size={20} color="#fff"/>
            <Text style={{ color:'#fff', fontWeight:'900', fontSize:15 }}>Export & Share Report</Text>
          </TouchableOpacity>
          <TouchableOpacity style={{ backgroundColor:'#F3F4F6', borderRadius:14, padding:13, flexDirection:'row', justifyContent:'center', gap:8, alignItems:'center' }}
            onPress={()=>Alert.alert('PDF Export','PDF export will download the report. This requires a PDF library (expo-print) to be installed.')}>
            <MaterialCommunityIcons name="file-pdf-box" size={18} color="#EF4444"/>
            <Text style={{ color:'#374151', fontWeight:'700', fontSize:14 }}>Export as PDF</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    );
  };

  // ── Tab: BENEFITS ─────────────────────────────────────────────────────────
  const renderBenefitsTab = () => {
    const plans = safeArr(benefitsData.plans);
    const filtered = benefitFilter === 'all' ? plans : plans.filter(p=>p.type===benefitFilter);
    const typeIcons = { Health:'heart-pulse', Dental:'tooth', Vision:'eye', Pension:'piggy-bank', Life:'shield-account', Wellness:'dumbbell', Family:'account-child' };
    const totalMonthlyCost = plans.reduce((s,p)=>s+((parseInt(p.cost)||0) * (p.enrolled||0)),0);
    const types = [...new Set(plans.map(p=>p.type))];
    return (
      <View style={{ flex:1 }}>
        <View style={{ backgroundColor:'#fff', paddingHorizontal:16, paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#E5E7EB' }}>
          <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10, marginBottom:10 }}>
            <StatCard title="Benefit Plans" value={plans.length} icon="gift" color="#059669" subtitle="Active plans" />
            <StatCard title="Monthly Cost" value={fmtMoney(totalMonthlyCost)} icon="currency-usd" color="#0e4e96" subtitle="Total enrollment cost" />
            <StatCard title="Budget Used" value={`${benefitsData.budget>0?pct(benefitsData.utilized,benefitsData.budget):87}%`} icon="chart-pie" color="#F59E0B" subtitle="Of HR benefits budget" />
            <StatCard title="Avg Enrollment" value={`${plans.length?Math.round((plans||[]).reduce((s,p)=>s+(p.enrolled||0),0)/plans.length):0}`} icon="account-group" color="#8b5cf6" subtitle="Per plan" />
          </View>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:6, marginBottom:4 }}>
            {['all',...types].map(t=>(
              <TouchableOpacity key={t} onPress={()=>setBenefitFilter(t)}
                style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:benefitFilter===t?'#059669':'#F3F4F6', borderWidth:1, borderColor:benefitFilter===t?'#059669':'#E5E7EB' }}>
                <Text style={{ fontSize:12, fontWeight:'600', color:benefitFilter===t?'#fff':'#6B7280' }}>{t==='all'?'All':t}</Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity onPress={()=> can('create_benefit') ? setModal('create_benefit') : Alert.alert('Restricted', 'Benefit management is restricted.')}
              style={{ paddingHorizontal:14, paddingVertical:7, borderRadius:16, backgroundColor:'#059669', flexDirection:'row', alignItems:'center', gap:4 }}>
              <MaterialCommunityIcons name="plus" size={14} color="#fff"/>
              <Text style={{ fontSize:12, fontWeight:'700', color:'#fff' }}>Add Plan</Text>
            </TouchableOpacity>
          </ScrollView>
        </View>
        <FlatList data={filtered} keyExtractor={(p,i)=>String(p.id||i)}
          contentContainerStyle={{ padding:14, paddingBottom:80 }}
          ListEmptyComponent={<View style={{ alignItems:'center', paddingVertical:60 }}><MaterialCommunityIcons name="gift-outline" size={56} color="#E5E7EB"/><Text style={{ color:'#9CA3AF', marginTop:12 }}>No benefit plans</Text></View>}
          renderItem={({item:plan,index:i})=>{
            const enrollPct = (employees||[]).length ? pct(plan.enrolled||0, (employees||[]).length) : 0;
            const icon = typeIcons[plan.type] || 'gift';
            const monthlyCost = (parseInt(plan.cost)||0) * (plan.enrolled||0);
            return (
              <View style={[sty.empCard, { marginBottom:12 }]}>
                <View style={{ flexDirection:'row', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                  <View style={{ width:52, height:52, borderRadius:14, backgroundColor:'#ECFDF5', alignItems:'center', justifyContent:'center' }}>
                    <MaterialCommunityIcons name={icon} size={26} color="#059669"/>
                  </View>
                  <View style={{ flex:1 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:3 }}>
                      <Text style={{ fontSize:15, fontWeight:'800', color:'#111827', flex:1 }}>{plan.planName}</Text>
                      <Pill label={plan.type} color="#059669" small/>
                    </View>
                    <Text style={{ fontSize:12, color:'#6B7280' }}>Provider: {plan.provider||'—'} · Eligibility: {plan.eligibility||'All'}</Text>
                    <View style={{ flexDirection:'row', gap:12, marginTop:6 }}>
                      <Text style={{ fontSize:12, fontWeight:'700', color:'#0e4e96' }}>${parseInt(plan.cost)||0}/mo per employee</Text>
                      <Text style={{ fontSize:12, fontWeight:'700', color:'#EF4444' }}>Total: {fmtMoney(monthlyCost)}/mo</Text>
                    </View>
                  </View>
                </View>
                <View>
                  <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
                    <Text style={{ fontSize:12, color:'#374151', fontWeight:'600' }}>Enrollment</Text>
                    <Text style={{ fontSize:12, fontWeight:'800', color:'#059669' }}>{plan.enrolled||0}/{(employees||[]).length} ({enrollPct}%)</Text>
                  </View>
                  <ProgressBar value={plan.enrolled||0} max={(employees||[]).length||1} color='#059669' height={7}/>
                  {plan.coverage && <Text style={{ fontSize:11, color:'#9CA3AF', marginTop:5 }}>Coverage: {plan.coverage}</Text>}
                </View>
              </View>
            );
          }}
        />
      </View>
    );
  };

  // ── Tab: OVERVIEW ─────────────────────────────────────────────────────────
  const renderOverviewTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}>

      {/* KPI grid */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Total Employees"   value={overview.totalEmployees}    icon="account-group"    color="#0e4e96"  subtitle="All staff"        trend={2.3}  onPress={()=>setActiveTab('employees')} />
        <StatCard title="Active"            value={overview.activeEmployees}   icon="account-check"   color="#10b981"  subtitle="Currently working" trend={1.8} />
        <StatCard title="On Leave"          value={overview.onLeave}           icon="account-clock"   color="#f59e0b"  subtitle="Absent today"      trend={-5.2} />
        <StatCard title="New Hires (30d)"   value={overview.newHires}          icon="account-plus"    color="#8b5cf6"  subtitle="Last 30 days"      trend={15.7} />
        <StatCard title="Pending Leave"     value={overview.pendingLeave}      icon="calendar-clock"  color="#f97316"  subtitle="Awaiting approval" onPress={()=>{setActiveTab('leave');setLeaveFilter('pending')}} />
        <StatCard title="Attendance Rate"   value={`${overview.attendanceRate}%`} icon="calendar-check" color="#06b6d4" subtitle="This month"       trend={0.8} />
        <StatCard title="Avg Rating"        value={`${performanceData.averageRating}/5`} icon="star"  color="#f59e0b"  subtitle="Performance"       trend={2.1}  onPress={()=>setActiveTab('performance')} />
        <StatCard title="Warning Letters"   value={overview.warningLetters}    icon="alert-circle"    color="#dc2626"  subtitle="Active warnings" />
      </View>

      {/* Quick Actions */}
      <SectionCard title="Quick Actions">
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
          {[
            ...(can('add_employee') ? [{ label:'Add Employee',    icon:'account-plus',     color:'#10b981', bg:'#ECFDF5', onPress:async()=>{const cid=companyId||(await AsyncStorage.getItem('company_id').catch(()=>null));navigation?.navigate('AddEmployeeScreen',{companyId:cid,existingCount:(employees||[]).length,onSave:(emp)=>{setEmployees(prev=>[emp,...(prev||[])]);loadAllData(true);}})} }] : []),
            ...(can('approve_leave') ? [{ label:'Approve Leave',   icon:'calendar-check',   color:'#f59e0b', bg:'#FFFBEB', onPress:()=>{setActiveTab('leave');setLeaveFilter('pending')} }] : []),
            ...(can('process_payroll') ? [{ label:'Process Payroll', icon:'cash-multiple',    color:'#0e4e96', bg:'#EFF6FF', onPress:()=>setModal('process_payroll') }] : []),
            ...(can('schedule_review') ? [{ label:'Schedule Review', icon:'clipboard-check',  color:'#8b5cf6', bg:'#F5F3FF', onPress:()=>setModal('schedule_review') }] : []),
            ...(can('post_job') ? [{ label:'Post Job',        icon:'briefcase-plus',   color:'#06b6d4', bg:'#ECFEFF', onPress:()=>setModal('post_job') }] : []),
            ...(can('issue_warning') ? [{ label:'Issue Warning',   icon:'alert-circle',     color:'#dc2626', bg:'#FEF2F2', onPress:()=>setModal('issue_warning') }] : []),
            { label:'View Analytics',  icon:'chart-donut',      color:'#ec4899', bg:'#FDF2F8', onPress:()=>setActiveTab('analytics') },
            { label:'Export Report',   icon:'file-chart',       color:'#374151', bg:'#F9FAFB', onPress:exportReport },
            ...(can('full_employee_mgmt') ? [{ label:'Full Emp Mgmt',   icon:'account-cog',      color:'#0891B2', bg:'#ECFEFF', onPress:()=>navigation?.navigate('EmployeeManagementScreen',{companyId}) }] : []),
            ...(can('team_management') ? [{ label:'Team View',       icon:'account-group',    color:'#7C3AED', bg:'#F5F3FF', onPress:()=>navigation?.navigate('TeamManagementScreen',{companyId}) }] : []),
            ...(can('view_financials') ? [{ label:'Financials',      icon:'chart-bar',        color:'#059669', bg:'#ECFDF5', onPress:()=>navigation?.navigate('FinancialManagement',{companyId}) }] : []),
            ...(can('visit_tracking') ? [{ label:'Visit Tracking',  icon:'map-marker-path',  color:'#0284C7', bg:'#E0F2FE', onPress:()=>navigation?.navigate('VisitTrackingScreen',{ companyProfile: { name: companyName, logo: companyLogo, company_name: companyName }, companyId }) }] : []),
            ...(can('follow_up_reminders') ? [{ label:'Follow-Ups',      icon:'bell-ring',        color:'#D97706', bg:'#FEF3C7', onPress:()=>navigation?.navigate('FollowUpRemindersScreen',{ companyProfile: { name: companyName, logo: companyLogo, company_name: companyName }, companyId }) }] : []),
            ...(isAdminAuthorized ? [{ label:'Admin Panel',     icon:'shield-crown',     color:'#B45309', bg:'#FEF3C7', onPress:()=>navigation?.navigate('AdminHRManagementScreen',{ companyProfile: { name: companyName, logo: companyLogo, company_name: companyName }, companyId, user: currentUser }) }] : []),
          ].map((a, i) => (
            <TouchableOpacity key={i} style={{ width: (width - 70) / 4, backgroundColor: a.bg, borderRadius: 14, padding: 12, alignItems: 'center', borderWidth: 1, borderColor: a.color + '25' }} onPress={a.onPress}>
              <MaterialCommunityIcons name={a.icon} size={22} color={a.color} />
              <Text style={{ fontSize: 10, fontWeight: '700', color: a.color, marginTop: 6, textAlign: 'center' }}>{a.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </SectionCard>

      {/* Payroll snapshot */}
      <TouchableOpacity style={[sty.sectionCard, { padding: 0, overflow: 'hidden' }]} onPress={() => setActiveTab('payroll')}>
        <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={{ padding: 18 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <View>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>Monthly Payroll Cost</Text>
              <Text style={{ color: '#fff', fontSize: 28, fontWeight: '900', marginTop: 2 }}>{fmtMoney(payrollData.totalPayroll)}</Text>
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>Avg: {fmtMoney(payrollData.averageSalary)} per employee</Text>
            </View>
            <View style={{ alignItems: 'flex-end', gap: 8 }}>
              <View style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: 10 }}>
                <MaterialCommunityIcons name="cash-multiple" size={28} color="#fff" />
              </View>
              {payrollData.pendingPayments > 0 && (
                <View style={{ backgroundColor: '#EF4444', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4 }}>
                  <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>{payrollData.pendingPayments} PENDING</Text>
                </View>
              )}
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 16, marginTop: 14, paddingTop: 14, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.2)' }}>
            {[['Net', payrollData.netPayroll], ['Taxes', payrollData.taxes], ['Benefits', payrollData.benefits]].map(([l, v]) => (
              <View key={l}>
                <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>{l}</Text>
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>{fmtMoney(v)}</Text>
              </View>
            ))}
          </View>
        </LinearGradient>
      </TouchableOpacity>

      {/* Department breakdown */}
      <SectionCard title="Department Breakdown" action="View All →" onAction={() => setActiveTab('employees')}>
        {(deptStats||[]).slice(0, 5).map(([dept, stats]) => (
          <View key={dept} style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: DEPT_COLORS[dept] || '#0e4e96' }} />
                <Text style={{ fontSize: 13, color: '#374151', fontWeight: '600' }}>{dept}</Text>
              </View>
              <Text style={{ fontSize: 12, fontWeight: '700', color: DEPT_COLORS[dept] || '#0e4e96' }}>{stats.count} · {fmtMoney(stats.totalSalary)}</Text>
            </View>
            <ProgressBar value={stats.count} max={(employees||[]).length} color={DEPT_COLORS[dept] || '#0e4e96'} height={6} />
          </View>
        ))}
      </SectionCard>

      {/* Notifications */}
      <SectionCard title={`Notifications${unreadNotifs > 0 ? ` (${unreadNotifs} unread)` : ''}`} action="View All →" onAction={() => can('view_notifications') ? setModal('notification_center') : Alert.alert('Restricted','Notification access is restricted.')}>
        {(notifications||[]).slice(0, 3).map((n, i) => {
          const typeColor = { urgent:'#dc2626', warning:'#f59e0b', success:'#10b981', info:'#0e4e96' }[n.type] || '#6B7280';
          return (
            <TouchableOpacity key={n.id || i} style={{ flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderBottomWidth: i < 2 ? 1 : 0, borderBottomColor: '#F1F5F9', gap: 10 }}
              onPress={() => setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))}>
              <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: typeColor + '15', alignItems: 'center', justifyContent: 'center' }}>
                <MaterialCommunityIcons name={n.type === 'urgent' ? 'alert-circle' : n.type === 'success' ? 'check-circle' : n.type === 'warning' ? 'alert' : 'information'} size={18} color={typeColor} />
              </View>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827', flex: 1 }} numberOfLines={1}>{n.title}</Text>
                  {!n.read && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#0e4e96' }} />}
                </View>
                <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }} numberOfLines={2}>{n.message}</Text>
                <Text style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{n.timestamp ? timeAgo(n.timestamp) : ''}</Text>
              </View>
            </TouchableOpacity>
          );
        })}
        {(notifications||[]).length === 0 && (
          <View style={{ alignItems: 'center', paddingVertical: 24 }}>
            <MaterialCommunityIcons name="bell-check" size={40} color="#E5E7EB" />
            <Text style={{ color: '#9CA3AF', marginTop: 8 }}>All caught up!</Text>
          </View>
        )}
      </SectionCard>

      {/* Compliance / Risks strip */}
      <SectionCard title="HR Health">
        {[
          { label: 'Compliance Score',   val: `${overview.complianceScore || 97}%`,  color: '#10b981', icon: 'shield-check', pct: overview.complianceScore || 97 },
          { label: 'Budget Utilization', val: `${overview.budgetUtilization || 87}%`, color: '#F59E0B', icon: 'currency-usd', pct: overview.budgetUtilization || 87 },
          { label: 'Training Complete',  val: `${trainingData.complianceRate}%`,      color: '#8B5CF6', icon: 'school', pct: trainingData.complianceRate },
          { label: 'Satisfaction Score', val: `${overview.satisfactionScore || 4.2}/5`, color: '#EC4899', icon: 'heart', pct: ((overview.satisfactionScore || 4.2) / 5) * 100 },
        ].map((m, i) => (
          <View key={i} style={{ marginBottom: 12 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                <MaterialCommunityIcons name={m.icon} size={14} color={m.color} />
                <Text style={{ fontSize: 12, color: '#374151', fontWeight: '600' }}>{m.label}</Text>
              </View>
              <Text style={{ fontSize: 12, fontWeight: '800', color: m.color }}>{m.val}</Text>
            </View>
            <ProgressBar value={m.pct} max={100} color={m.color} height={6} />
          </View>
        ))}
      </SectionCard>
    </ScrollView>
  );

  // ── Tab: EMPLOYEES ────────────────────────────────────────────────────────
  const renderEmployeesTab = () => (
    <View style={{ flex: 1 }}>
      {/* Search + filters */}
      <View style={{ paddingHorizontal: 16, paddingTop: 12, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
        <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10 }}>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: '#F3F4F6', borderRadius: 12, paddingHorizontal: 12, paddingVertical: 9, gap: 8 }}>
            <MaterialCommunityIcons name="magnify" size={18} color="#9CA3AF" />
            <TextInput style={{ flex: 1, fontSize: 14, color: '#111827' }} value={searchQ} onChangeText={setSearchQ} placeholder="Search by name, email, skill…" placeholderTextColor="#D1D5DB" />
            {searchQ ? <TouchableOpacity onPress={() => setSearchQ('')}><Ionicons name="close-circle" size={16} color="#9CA3AF" /></TouchableOpacity> : null}
          </View>
          <TouchableOpacity style={{ backgroundColor: showFilters ? '#0e4e96' : '#F3F4F6', borderRadius: 12, padding: 10 }} onPress={() => setShowFilters(v => !v)}>
            <MaterialCommunityIcons name="filter-variant" size={20} color={showFilters ? '#fff' : '#0e4e96'} />
          </TouchableOpacity>
          <TouchableOpacity style={{ backgroundColor: bulkMode?'#EF4444':'#F3F4F6', borderRadius: 12, padding: 10 }} 
            onPress={() => can('bulk_actions') ? (() => { setBulkMode(v=>!v); setSelectedBulk([]); })() : Alert.alert('Restricted', 'Bulk actions are restricted.')}>
            <MaterialCommunityIcons name={bulkMode?"close":"checkbox-multiple-marked-outline"} size={20} color={bulkMode?'#fff':'#374151'} />
          </TouchableOpacity>
          {bulkMode && selectedBulk.length > 0 && (
            <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 12, padding: 10 }} onPress={() => setShowBulkModal(true)}>
              <MaterialCommunityIcons name="cog-box" size={20} color="#fff" />
            </TouchableOpacity>
          )}
          {can('add_employee') && <TouchableOpacity style={{ backgroundColor: '#10b981', borderRadius: 12, padding: 10 }} onPress={async()=>{const cid=companyId||(await AsyncStorage.getItem('company_id').catch(()=>null));navigation?.navigate('AddEmployeeScreen',{companyId:cid,existingCount:(employees||[]).length,onSave:(emp)=>{setEmployees(prev=>[emp,...(prev||[])]);}})}}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>}
        </View>
        {/* Sort chips */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6, paddingBottom: 10 }}>
          {[['name','Name'],['salary','Salary'],['department','Dept'],['rating','Rating'],['startDate','Newest']].map(([k, l]) => (
            <TouchableOpacity key={k} onPress={() => setSortBy(k)} style={{ paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: sortBy === k ? '#0e4e96' : '#F3F4F6', borderWidth: 1, borderColor: sortBy === k ? '#0e4e96' : '#E5E7EB' }}>
              <Text style={{ fontSize: 11, fontWeight: '600', color: sortBy === k ? '#fff' : '#6B7280' }}>{l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        {/* Filters */}
        {showFilters && (
          <View style={{ paddingBottom: 10 }}>
            {[['department', departments], ['status', empStatuses], ['location', empLocations]].map(([key, opts]) => (
              <View key={key} style={{ marginBottom: 8 }}>
                <Text style={{ fontSize: 10, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 5 }}>{key}</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 5 }}>
                  {opts.map(opt => (
                    <TouchableOpacity key={opt} onPress={() => setFilters(p => ({ ...p, [key]: opt }))}
                      style={{ paddingHorizontal: 10, paddingVertical: 5, borderRadius: 10, backgroundColor: filters[key] === opt ? '#0e4e96' : '#F3F4F6', borderWidth: 1, borderColor: filters[key] === opt ? '#0e4e96' : '#E5E7EB' }}>
                      <Text style={{ fontSize: 11, color: filters[key] === opt ? '#fff' : '#6B7280', fontWeight: '600' }}>{opt}</Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            ))}
          </View>
        )}
      </View>
      <View style={{ paddingHorizontal: 16, paddingVertical: 8, backgroundColor: '#F9FAFB' }}>
        <Text style={{ fontSize: 13, color: '#6B7280' }}>Showing <Text style={{ fontWeight: '700', color: '#111827' }}>{filteredEmployees.length}</Text> of {(employees||[]).length} employees</Text>
      </View>
      <FlatList
        data={filteredEmployees}
        keyExtractor={e => String(e.id)}
        contentContainerStyle={{ padding: 14, paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
        ListEmptyComponent={<View style={{ alignItems: 'center', paddingVertical: 60 }}><MaterialCommunityIcons name="account-search" size={56} color="#E5E7EB" /><Text style={{ color: '#9CA3AF', marginTop: 12 }}>No employees found</Text><TouchableOpacity style={{ marginTop:12, backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:16, paddingVertical:8 }} onPress={()=>loadAllData(true)}><Text style={{ color:'#fff', fontWeight:'700' }}>Refresh</Text></TouchableOpacity></View>}
          ListFooterComponent={() => employees.length > 0 ? (
            <TouchableOpacity style={{ margin:4, marginBottom:16, backgroundColor:'#EFF6FF', borderRadius:12, padding:14, flexDirection:'row', alignItems:'center', gap:10, borderWidth:1, borderColor:'#BFDBFE' }}
              onPress={()=>navigation?.navigate('EmployeeManagementScreen',{companyId})}>
              <MaterialCommunityIcons name="account-cog" size={20} color="#0e4e96"/>
              <Text style={{ fontSize:13, fontWeight:'700', color:'#0e4e96', flex:1 }}>Open Full Employee Management</Text>
              <MaterialCommunityIcons name="arrow-right" size={16} color="#0e4e96"/>
            </TouchableOpacity>
          ) : null}
        renderItem={({ item: e }) => (
          <TouchableOpacity style={[sty.empCard, bulkMode && selectedBulk.includes(e.id) && { borderWidth:2, borderColor:'#0e4e96' }]}
            onPress={() => {
              if (bulkMode) {
                setSelectedBulk(prev => prev.includes(e.id) ? prev.filter(x=>x!==e.id) : [...prev,e.id]);
              } else {
                if(can('view_employee_details')){setSelectedEmp({ ...e, id: e.serverId || e.id }); setModal('emp_detail');}else{Alert.alert('Restricted','Employee detail view is restricted.');}
              }
            }}
            onLongPress={() => { setBulkMode(true); setSelectedBulk([e.id]); }}>
            {bulkMode && (
              <View style={{ position:'absolute', top:10, right:10, zIndex:10, width:24, height:24, borderRadius:12,
                backgroundColor:selectedBulk.includes(e.id)?'#0e4e96':'#F3F4F6',
                borderWidth:2, borderColor:selectedBulk.includes(e.id)?'#0e4e96':'#D1D5DB',
                alignItems:'center', justifyContent:'center' }}>
                {selectedBulk.includes(e.id) && <MaterialCommunityIcons name="check" size={14} color="#fff"/>}
              </View>
            )}
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
              <Avatar name={e.name} uri={e.avatar} size={52} />
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <Text style={{ fontSize: 15, fontWeight: '800', color: '#111827', flex: 1 }} numberOfLines={1}>{e.name}</Text>
                  <View style={{ backgroundColor: (STATUS_COLORS[e.status] || '#9CA3AF') + '20', borderRadius: 8, paddingHorizontal: 7, paddingVertical: 2 }}>
                    <Text style={{ fontSize: 9, fontWeight: '700', color: STATUS_COLORS[e.status] || '#9CA3AF' }}>{e.status}</Text>
                  </View>
                </View>
                <Text style={{ fontSize: 12, color: '#6B7280', marginBottom: 4 }}>{e.position} · {e.department}</Text>
                <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
                  <Text style={{ fontSize: 11, color: '#10b981', fontWeight: '600' }}>{fmtMoney(e.salary)}/yr</Text>
                  <Text style={{ fontSize: 11, color: '#6B7280' }}>📍 {e.location}</Text>
                  <Text style={{ fontSize: 11, color: e.attendanceRate >= 95 ? '#10b981' : e.attendanceRate >= 90 ? '#F59E0B' : '#EF4444', fontWeight: '600' }}>{e.attendanceRate}% att.</Text>
                </View>
                {e.skills.length > 0 && (
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 4, marginTop: 7 }}>
                    {e.skills.slice(0, 4).map((s, i) => (
                      <View key={i} style={{ backgroundColor: '#EFF6FF', borderRadius: 6, paddingHorizontal: 7, paddingVertical: 2 }}>
                        <Text style={{ fontSize: 9, color: '#3B82F6', fontWeight: '600' }}>{s}</Text>
                      </View>
                    ))}
                  </ScrollView>
                )}
              </View>
              {/* Stars */}
              <View style={{ alignItems: 'flex-end', gap: 4 }}>
                <View style={{ flexDirection: 'row' }}>
                  <Text>{[1,2,3,4,5].map(s => <MaterialCommunityIcons key={s} name={s <= e.performanceRating ? 'star' : 'star-outline'} size={12} color="#F59E0B" />)}</Text>
                </View>
                {e.warningCount > 0 && <View style={{ backgroundColor: '#FEF2F2', borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 }}>
                  <Text style={{ fontSize: 9, color: '#EF4444', fontWeight: '700' }}>⚠ {e.warningCount}</Text>
                </View>}
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 12, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#F8FAFC' }}>
              {[
                ...(can('view_employee_details') ? [{ label:'Details', color:'#8b5cf6', onPress:()=>{setSelectedEmp(e);setModal('emp_detail');} }] : []),
                ...(can('issue_warning') ? [{ label:'Warning', color:'#dc2626', onPress:()=>{setWarningForm(p=>({...p,employeeId:e.serverId||e.id,employeeName:e.name}));setModal('issue_warning');} }] : []),
                ...(can('schedule_review') ? [{ label:'Review',  color:'#f59e0b', onPress:()=>{setReviewForm(p=>({...p,employeeId:e.serverId||e.id,employeeName:e.name}));setModal('schedule_review');} }] : []),
                ...(can('view_employee_details') ? [{ label:'Navigate',color:'#0e4e96', onPress:()=>navigation?.navigate('EmployeeDetailsScreen',{employee:{ ...e, id: e.serverId || e.id },employeeId:e.serverId || e.id}) }] : []),
              ].map(btn => (
                <TouchableOpacity key={btn.label} style={{ flex: 1, backgroundColor: btn.color + '18', borderRadius: 8, paddingVertical: 7, alignItems: 'center', borderWidth: 1, borderColor: btn.color + '30' }} onPress={btn.onPress}>
                  <Text style={{ fontSize: 10, fontWeight: '700', color: btn.color }}>{btn.label}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </TouchableOpacity>
        )}
      />
    </View>
  );

  // ── Tab: PAYROLL ──────────────────────────────────────────────────────────
  const renderPayrollTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Total Payroll"      value={fmtMoney(payrollData.totalPayroll)}  icon="cash-multiple"  color="#10b981" subtitle="This month" trend={2.1} wide />
        <StatCard title="Average Salary"     value={fmtMoney(payrollData.averageSalary)} icon="cash"           color="#0e4e96" subtitle="Per employee" trend={1.5} />
        <StatCard title="Pending Payments"   value={payrollData.pendingPayments}         icon="clock-outline"  color="#f59e0b" subtitle="Need processing" />
        <StatCard title="Net Payroll"        value={fmtMoney(payrollData.netPayroll)}    icon="bank-transfer"  color="#8b5cf6" subtitle="After deductions" trend={1.8} />
      </View>

      <SectionCard title="Payroll Breakdown">
        {[
          { label:'Base Salaries', value: payrollData.totalPayroll, icon:'cash', color:'#111827', positive: true },
          { label:'Bonuses',       value: payrollData.bonuses,      icon:'gift', color:'#10b981', positive: true },
          { label:'Overtime Pay',  value: payrollData.overtimePay,  icon:'clock-plus', color:'#0EA5E9', positive: true },
          { label:'Benefits',      value: payrollData.benefits,     icon:'heart', color:'#8b5cf6', positive: true },
          { label:'Deductions',    value: payrollData.deductions,   icon:'minus-circle', color:'#EF4444', positive: false },
          { label:'Taxes',         value: payrollData.taxes,        icon:'office-building', color:'#EF4444', positive: false },
        ].map((item, i, arr) => (
          <View key={item.label} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 13, borderBottomWidth: i < arr.length - 1 ? 1 : 0, borderBottomColor: '#F8FAFC' }}>
            <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: item.color + '15', alignItems: 'center', justifyContent: 'center', marginRight: 12 }}>
              <MaterialCommunityIcons name={item.icon} size={18} color={item.color} />
            </View>
            <Text style={{ flex: 1, fontSize: 14, color: '#374151', fontWeight: '600' }}>{item.label}</Text>
            <Text style={{ fontSize: 15, fontWeight: '800', color: item.positive ? '#111827' : '#EF4444' }}>
              {item.positive ? '' : '−'}{fmtMoney(item.value)}
            </Text>
          </View>
        ))}
        <View style={{ flexDirection: 'row', alignItems: 'center', paddingTop: 14, marginTop: 4, borderTopWidth: 2, borderTopColor: '#0e4e96' }}>
          <Text style={{ flex: 1, fontSize: 16, fontWeight: '800', color: '#0e4e96' }}>Net Payroll</Text>
          <Text style={{ fontSize: 20, fontWeight: '900', color: '#0e4e96' }}>{fmtMoney(payrollData.netPayroll)}</Text>
        </View>
      </SectionCard>

      {/* Payroll by department */}
      <SectionCard title="Payroll by Department">
        {(deptStats||[]).slice(0, 6).map(([dept, stats]) => (
          <View key={dept} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8 }}>
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: DEPT_COLORS[dept] || '#0e4e96', marginRight: 10 }} />
            <Text style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: '600' }}>{dept}</Text>
            <Text style={{ fontSize: 11, color: '#6B7280', marginRight: 12 }}>{stats.count} emp</Text>
            <Text style={{ fontSize: 13, fontWeight: '700', color: DEPT_COLORS[dept] || '#0e4e96' }}>{fmtMoney(stats.totalSalary)}</Text>
          </View>
        ))}
      </SectionCard>

      {/* Process payroll CTA */}
      <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 14, padding: 16, flexDirection: 'row', justifyContent: 'center', gap: 10, alignItems: 'center', marginBottom: 8 }}
        onPress={() => can('process_payroll') ? setModal('process_payroll') : Alert.alert('Restricted', 'Payroll processing is restricted.')}>
        <MaterialCommunityIcons name="cash-multiple" size={20} color="#fff" />
        <Text style={{ color: '#fff', fontWeight: '900', fontSize: 15 }}>Process Payroll Now</Text>
      </TouchableOpacity>
      <TouchableOpacity style={{ backgroundColor: '#F3F4F6', borderRadius: 14, padding: 14, flexDirection: 'row', justifyContent: 'center', gap: 10, alignItems: 'center', marginBottom: 80 }}
        onPress={() => can('navigate_payroll_mgmt') ? navigation?.navigate('PayrollManagement', { companyId }) : Alert.alert('Restricted', 'Payroll management access is restricted.')}>
        <MaterialCommunityIcons name="arrow-right" size={18} color="#374151" />
        <Text style={{ color: '#374151', fontWeight: '700', fontSize: 14 }}>Open Full Payroll Screen</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  // ── Tab: PERFORMANCE ──────────────────────────────────────────────────────
  const renderPerformanceTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#8b5cf6']} />}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Reviews Completed" value={performanceData.reviewsCompleted} icon="check-circle"  color="#10b981" subtitle="This quarter" trend={8.3} />
        <StatCard title="Reviews Pending"   value={performanceData.reviewsPending}   icon="clock-outline" color="#f59e0b" subtitle="Need attention" />
        <StatCard title="Average Rating"    value={`${performanceData.averageRating}/5`} icon="star"     color="#8b5cf6" subtitle="Company-wide"  trend={2.1} />
        <StatCard title="Top Performers"    value={performanceData.topPerformers}    icon="trophy"        color="#f59e0b" subtitle="5-star ratings" />
        <StatCard title="Improvement Plans" value={performanceData.improvementPlans} icon="chart-line-variant" color="#EF4444" subtitle="Active PIPs" />
        <StatCard title="Goal Completion"   value={`${performanceData.goalCompletion || 78}%`} icon="target" color="#06b6d4" subtitle="All employees" trend={3.1} />
      </View>

      {/* Rating distribution */}
      <SectionCard title="Performance Distribution">
        {[5,4,3,2,1].map(star => {
          const count = (employees||[]).filter(e => Math.round(e.performanceRating) === star).length;
          const pctVal = (employees||[]).length ? pct(count, (employees||[]).length) : 0;
          const c = { 5:'#059669', 4:'#10b981', 3:'#F59E0B', 2:'#F97316', 1:'#EF4444' }[star];
          return (
            <View key={star} style={{ marginBottom: 10 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', marginRight: 8 }}>
                  <Text>{[1,2,3,4,5].map(s => <MaterialCommunityIcons key={s} name={s <= star ? 'star' : 'star-outline'} size={13} color="#F59E0B" />)}</Text>
                </View>
                <Text style={{ flex: 1, fontSize: 12, color: '#374151' }}>{star} Star{star > 1 ? 's' : ''}</Text>
                <Text style={{ fontSize: 12, fontWeight: '700', color: c }}>{count} ({pctVal}%)</Text>
              </View>
              <ProgressBar value={count} max={(employees||[]).length || 1} color={c} height={7} />
            </View>
          );
        })}
      </SectionCard>

      {/* Recent reviews */}
      <SectionCard title="Recent Reviews" action="Schedule Review →" onAction={() => can('schedule_review') ? setModal('schedule_review') : Alert.alert('Restricted','Review scheduling is restricted.')}>
        {safeArr(performanceData.reviews).slice(0, 5).map((r, i) => {
          const rating = parseInt(r.overall_rating || r.overallRating || r.rating || 3);
          const rColor = rating >= 4 ? '#10b981' : rating >= 3 ? '#F59E0B' : '#EF4444';
          return (
            <View key={r.id || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: i < 4 ? 1 : 0, borderBottomColor: '#F8FAFC', gap: 10 }}>
              <Avatar name={r.employee_name || r.employeeName || '?'} size={40} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827' }}>{r.employee_name || r.employeeName || '—'}</Text>
                <Text style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{r.review_period || r.period || r.type || 'Annual'} · {r.reviewer || '—'}</Text>
              </View>
              <View style={{ alignItems: 'center' }}>
                <View style={{ flexDirection: 'row' }}>
                  <Text>{[1,2,3,4,5].map(s => <MaterialCommunityIcons key={s} name={s <= rating ? 'star' : 'star-outline'} size={12} color="#F59E0B" />)}</Text>
                </View>
                <Text style={{ fontSize: 10, fontWeight: '700', color: rColor, marginTop: 2 }}>{rating}/5</Text>
              </View>
            </View>
          );
        })}
        {safeArr(performanceData.reviews).length === 0 && (
          <View style={{ alignItems: 'center', paddingVertical: 32 }}>
            <MaterialCommunityIcons name="chart-line" size={40} color="#E5E7EB" />
            <Text style={{ color: '#9CA3AF', marginTop: 8 }}>No reviews yet</Text>
            <TouchableOpacity style={{ backgroundColor: '#8b5cf6', borderRadius: 12, paddingHorizontal: 18, paddingVertical: 9, marginTop: 12 }} onPress={() => can('schedule_review') ? setModal('schedule_review') : Alert.alert('Restricted', 'Performance review scheduling is restricted.')}>
              <Text style={{ color: '#fff', fontWeight: '700' }}>Schedule First Review</Text>
            </TouchableOpacity>
          </View>
        )}
      </SectionCard>

      {/* Top performers */}
      <SectionCard title="Top Performers">
        {(employees||[]).filter(e => e.performanceRating >= 4).slice(0, 5).map((e, i) => (
          <View key={e.id || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: i < 4 ? 1 : 0, borderBottomColor: '#F8FAFC', gap: 10 }}>
            <Text style={{ fontSize: 16, fontWeight: '900', color: i === 0 ? '#F59E0B' : i === 1 ? '#9CA3AF' : i === 2 ? '#CD7F32' : '#6B7280', width: 24, textAlign: 'center' }}>#{i + 1}</Text>
            <Avatar name={e.name} uri={e.avatar} size={40} />
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827' }}>{e.name}</Text>
              <Text style={{ fontSize: 11, color: '#6B7280' }}>{e.position} · {e.department}</Text>
            </View>
            <View style={{ flexDirection: 'row' }}>
              <Text>{[1,2,3,4,5].map(s => <MaterialCommunityIcons key={s} name={s <= e.performanceRating ? 'star' : 'star-outline'} size={13} color="#F59E0B" />)}</Text>
            </View>
          </View>
        ))}
        {(employees||[]).filter(e => e.performanceRating >= 4).length === 0 && (
          <Text style={{ color: '#9CA3AF', textAlign: 'center', paddingVertical: 20 }}>No performance data yet</Text>
        )}
      </SectionCard>
    </ScrollView>
  );

  // ── Tab: LEAVE ────────────────────────────────────────────────────────────
  const renderLeaveTab = () => (
    <View style={{ flex: 1 }}>
      <View style={{ backgroundColor: '#fff', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
          <StatCard title="Total Requests" value={(leaveRequests||[]).length}                                        icon="calendar-clock"  color="#0e4e96" subtitle="All time" />
          <StatCard title="Pending"        value={(leaveRequests||[]).filter(l=>(l.status||'pending')==='pending').length} icon="clock-outline" color="#f59e0b" subtitle="Need approval" onPress={()=>setLeaveFilter('pending')} />
          <StatCard title="Approved"       value={(leaveRequests||[]).filter(l=>l.status==='approved').length}       icon="check-circle"    color="#10b981" subtitle="This month" />
          <StatCard title="Rejected"       value={(leaveRequests||[]).filter(l=>l.status==='rejected').length}       icon="close-circle"    color="#EF4444" subtitle="This month" />
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
          {[['all','All'],['pending','Pending'],['approved','Approved'],['rejected','Rejected']].map(([k, l]) => (
            <TouchableOpacity key={k} onPress={() => setLeaveFilter(k)} style={{ paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, backgroundColor: leaveFilter === k ? '#0e4e96' : '#F3F4F6', borderWidth: 1, borderColor: leaveFilter === k ? '#0e4e96' : '#E5E7EB' }}>
              <Text style={{ fontSize: 12, fontWeight: '600', color: leaveFilter === k ? '#fff' : '#6B7280' }}>{l}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      <FlatList
        data={filteredLeave}
        keyExtractor={(l, i) => String(l.id || i)}
        contentContainerStyle={{ padding: 14, paddingBottom: 80 }}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#f59e0b']} />}
        ListEmptyComponent={<View style={{ alignItems: 'center', paddingVertical: 60 }}><MaterialCommunityIcons name="calendar-check" size={56} color="#E5E7EB" /><Text style={{ color: '#9CA3AF', marginTop: 12 }}>No leave requests {leaveFilter !== 'all' ? `(${leaveFilter})` : ''}</Text></View>}
        renderItem={({ item: l }) => {
          const status = l.status || l.leave_status || 'pending';
          const sCfg = { pending: { color: '#F59E0B', bg: '#FFFBEB', label: 'Pending' }, approved: { color: '#10b981', bg: '#ECFDF5', label: 'Approved' }, rejected: { color: '#EF4444', bg: '#FEF2F2', label: 'Rejected' } }[status] || { color: '#6B7280', bg: '#F9FAFB', label: status };
          const leaveType = l.leave_type || l.type || l.leaveType || 'Annual';
          const leaveColor = LEAVE_COLORS[leaveType] || '#6B7280';
          return (
            <View style={sty.leaveCard}>
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
                <Avatar name={l.employee_name || l.candidateName || l.name || '?'} uri={l.avatar} size={48} />
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Text style={{ fontSize: 15, fontWeight: '800', color: '#111827', flex: 1 }} numberOfLines={1}>{l.employee_name || l.candidateName || l.name || '—'}</Text>
                    <View style={{ backgroundColor: sCfg.bg, borderRadius: 10, paddingHorizontal: 8, paddingVertical: 3 }}>
                      <Text style={{ fontSize: 10, fontWeight: '700', color: sCfg.color }}>{sCfg.label}</Text>
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 5 }}>
                    <View style={{ backgroundColor: leaveColor + '20', borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
                      <Text style={{ fontSize: 11, fontWeight: '700', color: leaveColor }}>{leaveType}</Text>
                    </View>
                    <Text style={{ fontSize: 12, color: '#6B7280' }}>{l.days || l.duration || 1} day{(l.days || 1) > 1 ? 's' : ''}</Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 4 }}>
                    {l.start_date || l.startDate ? <Text style={{ fontSize: 11, color: '#9CA3AF' }}>{fmtDate(l.start_date || l.startDate)} → {fmtDate(l.end_date || l.endDate || l.start_date || l.startDate)}</Text> : null}
                  </View>
                  {(l.reason || l.note) ? <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }} numberOfLines={2}>{l.reason || l.note}</Text> : null}
                </View>
              </View>
              {status === 'pending' && (
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
                  <TouchableOpacity style={{ flex: 1, backgroundColor: '#FEF2F2', borderRadius: 10, padding: 10, flexDirection: 'row', justifyContent: 'center', gap: 5, alignItems: 'center' }}
                    onPress={() => can('approve_leave') ? (() => { setSelectedLeave(l); setLeaveNote(''); setModal('leave_approve'); })() : Alert.alert('Restricted', 'You do not have permission to approve leave.')}>
                    <MaterialCommunityIcons name="close-circle" size={16} color="#EF4444" />
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#EF4444' }}>Reject</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={{ flex: 2, backgroundColor: '#ECFDF5', borderRadius: 10, padding: 10, flexDirection: 'row', justifyContent: 'center', gap: 5, alignItems: 'center' }}
                    onPress={() => can('approve_leave') ? Alert.alert(`Approve Leave?`, `Approve ${leaveType} leave for ${l.employee_name || l.name}?`, [{ text: 'Cancel', style: 'cancel' }, { text: 'Approve', onPress: () => { setSelectedLeave(l); handleApproveLeave(true); } }]) : Alert.alert('Restricted', 'Leave approval is restricted.')}>
                    <MaterialCommunityIcons name="check-circle" size={16} color="#10b981" />
                    <Text style={{ fontSize: 13, fontWeight: '700', color: '#10b981' }}>Approve</Text>
                  </TouchableOpacity>
                </View>
              )}
              {l.reviewed_note && <Text style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, fontStyle: 'italic' }}>Note: {l.reviewed_note}</Text>}
            </View>
          );
        }}
      />
    </View>
  );

  // ── Tab: RECRUITMENT ──────────────────────────────────────────────────────
  const renderRecruitmentTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#06b6d4']} />}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Active Jobs"    value={recruitmentData.activeJobs}    icon="briefcase-plus"    color="#0e4e96" subtitle="Open positions"  trend={12.5} />
        <StatCard title="Applications"  value={recruitmentData.applications}  icon="account-multiple"  color="#10b981" subtitle="Total received"   trend={18.7} />
        <StatCard title="Interviews"     value={recruitmentData.interviews}    icon="account-voice"     color="#8b5cf6" subtitle="Scheduled" />
        <StatCard title="Offers Made"    value={recruitmentData.offers}        icon="handshake"         color="#f59e0b" subtitle="Pending response" />
        <StatCard title="Hired"          value={recruitmentData.hired}         icon="account-check"     color="#059669" subtitle="This month"      trend={8.2} />
        <StatCard title="Time to Hire"   value={`${recruitmentData.timeToHire || 28}d`} icon="clock-fast" color="#06b6d4" subtitle="Average days" />
      </View>

      {/* Recruitment funnel */}
      <SectionCard title="Hiring Funnel">
        {[
          { label: 'Applications',    val: recruitmentData.applications, color: '#0e4e96' },
          { label: 'Screening',       val: Math.round(recruitmentData.applications * 0.35), color: '#8b5cf6' },
          { label: 'Interviews',      val: recruitmentData.interviews,  color: '#F59E0B' },
          { label: 'Offers',          val: recruitmentData.offers,      color: '#10b981' },
          { label: 'Hired',           val: recruitmentData.hired,       color: '#059669' },
        ].map((s, i, arr) => (
          <View key={s.label} style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 13, color: '#374151', fontWeight: '600' }}>{s.label}</Text>
              <Text style={{ fontSize: 13, fontWeight: '800', color: s.color }}>{s.val} ({pct(s.val, recruitmentData.applications || 1)}%)</Text>
            </View>
            <ProgressBar value={s.val} max={recruitmentData.applications || 1} color={s.color} height={8} />
          </View>
        ))}
      </SectionCard>

      {/* Active jobs */}
      <SectionCard title="Active Job Openings" action="+ Post Job" onAction={() => can('post_job') ? setModal('post_job') : Alert.alert('Restricted','Job posting is restricted.')}>
        {safeArr(recruitmentData.jobs).filter(j => j.status === 'active' || j.is_active).slice(0, 5).map((job, i) => (
          <TouchableOpacity key={job.id || i} style={{ paddingVertical: 12, borderBottomWidth: i < 4 ? 1 : 0, borderBottomColor: '#F8FAFC' }}
            onPress={() => navigation?.navigate('PostJobScreen', { jobId: job.id, companyId })}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
              <View style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: (DEPT_COLORS[job.department] || '#0e4e96') + '15', alignItems: 'center', justifyContent: 'center' }}>
                <MaterialCommunityIcons name="briefcase" size={20} color={DEPT_COLORS[job.department] || '#0e4e96'} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '700', color: '#111827' }}>{job.title || '—'}</Text>
                <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{job.department} · {job.location || 'Office'} · {job.type || 'Full-time'}</Text>
                <View style={{ flexDirection: 'row', gap: 6, marginTop: 6 }}>
                  {job.salary && <Pill label={job.salary} color="#10b981" small />}
                  <Pill label="Active" color="#10b981" small />
                </View>
              </View>
              <TouchableOpacity style={{ backgroundColor: '#EFF6FF', borderRadius: 8, padding: 6 }} onPress={() => navigation?.navigate('ApplicantsPoolScreen', { jobId: job.id, jobTitle: job.title, companyId })}>
                <MaterialCommunityIcons name="account-multiple" size={16} color="#3B82F6" />
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        ))}
        {safeArr(recruitmentData.jobs).filter(j => j.status === 'active' || j.is_active).length === 0 && (
          <View style={{ alignItems: 'center', paddingVertical: 32 }}>
            <MaterialCommunityIcons name="briefcase-outline" size={40} color="#E5E7EB" />
            <Text style={{ color: '#9CA3AF', marginTop: 8 }}>No active jobs</Text>
            <TouchableOpacity style={{ backgroundColor: '#06b6d4', borderRadius: 12, paddingHorizontal: 18, paddingVertical: 9, marginTop: 12 }} onPress={() => can('post_job') ? setModal('post_job') : Alert.alert('Restricted', 'Job posting is restricted.')}>
              <Text style={{ color: '#fff', fontWeight: '700' }}>Post First Job</Text>
            </TouchableOpacity>
          </View>
        )}
      </SectionCard>

      {/* Navigate to full recruitment */}
      <TouchableOpacity style={{ backgroundColor: '#F0FDFE', borderRadius: 14, padding: 16, flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 80, borderWidth: 1, borderColor: '#06b6d420' }}
        onPress={() => can('navigate_recruitment') ? navigation?.navigate('RecruitmentFlowComponent', { companyId }) : Alert.alert('Restricted', 'Recruitment access is restricted.')}>
        <MaterialCommunityIcons name="account-convert" size={24} color="#06b6d4" />
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 15, fontWeight: '800', color: '#0e4e96' }}>Open Full Recruitment Pipeline</Text>
          <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>Manage all stages, candidates and analytics</Text>
        </View>
        <Ionicons name="arrow-forward" size={20} color="#06b6d4" />
      </TouchableOpacity>
    </ScrollView>
  );

  // ── Tab: TRAINING ─────────────────────────────────────────────────────────
  const renderTrainingTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#ec4899']} />}>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Total Modules"     value={trainingData.totalModules}   icon="school"             color="#ec4899" subtitle="All programs" />
        <StatCard title="Completed"         value={trainingData.completed}      icon="check-circle"       color="#10b981" subtitle="Finished" trend={12} />
        <StatCard title="In Progress"       value={trainingData.inProgress}     icon="progress-check"     color="#3B82F6" subtitle="Ongoing" />
        <StatCard title="Overdue"           value={trainingData.overdue}        icon="alert-circle"       color="#EF4444" subtitle="Past due date" />
        <StatCard title="Compliance Rate"   value={`${trainingData.complianceRate}%`} icon="shield-check" color="#059669" subtitle="Mandatory training" trend={5.2} wide />
      </View>

      <SectionCard title="Training Compliance">
        <View style={{ marginBottom: 12 }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
            <Text style={{ fontSize: 13, fontWeight: '600', color: '#374151' }}>Overall Compliance</Text>
            <Text style={{ fontSize: 14, fontWeight: '900', color: trainingData.complianceRate >= 90 ? '#10b981' : trainingData.complianceRate >= 70 ? '#F59E0B' : '#EF4444' }}>{trainingData.complianceRate}%</Text>
          </View>
          <ProgressBar value={trainingData.complianceRate} max={100} color={trainingData.complianceRate >= 90 ? '#10b981' : trainingData.complianceRate >= 70 ? '#F59E0B' : '#EF4444'} height={10} />
          <Text style={{ fontSize: 11, color: '#9CA3AF', marginTop: 6 }}>{trainingData.completed} of {trainingData.totalModules} modules completed across the organisation</Text>
        </View>
        {[
          { label:'Compliance Training',    pct: trainingData.complianceRate || 85, color:'#EF4444' },
          { label:'Safety & Health',        pct: 92, color:'#F59E0B' },
          { label:'Leadership Development', pct: 67, color:'#8B5CF6' },
          { label:'Technical Skills',       pct: 78, color:'#3B82F6' },
          { label:'Soft Skills',            pct: 55, color:'#10B981' },
        ].map((cat, i) => (
          <View key={i} style={{ marginBottom: 10 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{ fontSize: 12, color: '#374151', fontWeight: '600' }}>{cat.label}</Text>
              <Text style={{ fontSize: 12, fontWeight: '700', color: cat.color }}>{cat.pct}%</Text>
            </View>
            <ProgressBar value={cat.pct} max={100} color={cat.color} height={6} />
          </View>
        ))}
      </SectionCard>

      {/* Training modules list */}
      <SectionCard title="Training Modules">
        {safeArr(trainingData.modules).length > 0 ? safeArr(trainingData.modules).slice(0, 8).map((m, i) => {
          const statusCfg = { completed: { color: '#10b981', bg: '#ECFDF5', label: '✓ Done' }, in_progress: { color: '#3B82F6', bg: '#EFF6FF', label: 'In Progress' }, not_started: { color: '#9CA3AF', bg: '#F9FAFB', label: 'Not Started' }, overdue: { color: '#EF4444', bg: '#FEF2F2', label: 'Overdue' } }[m.status] || { color: '#6B7280', bg: '#F9FAFB', label: m.status || 'Unknown' };
          return (
            <View key={m.id || i} style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: i < 7 ? 1 : 0, borderBottomColor: '#F8FAFC', gap: 10 }}>
              <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: statusCfg.color + '15', alignItems: 'center', justifyContent: 'center' }}>
                <MaterialCommunityIcons name={m.status === 'completed' ? 'check-circle' : 'book-open-variant'} size={18} color={statusCfg.color} />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827' }}>{m.title || m.name || '—'}</Text>
                <Text style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{m.category || 'General'} · Due {fmtDate(m.due_date || m.dueDate)}</Text>
                {m.status !== 'not_started' && <ProgressBar value={m.progress || 0} max={100} color={statusCfg.color} height={4} />}
              </View>
              <View style={{ backgroundColor: statusCfg.bg, borderRadius: 8, paddingHorizontal: 8, paddingVertical: 3 }}>
                <Text style={{ fontSize: 10, fontWeight: '700', color: statusCfg.color }}>{statusCfg.label}</Text>
              </View>
            </View>
          );
        }) : (
          <View style={{ alignItems: 'center', paddingVertical: 32 }}>
            <MaterialCommunityIcons name="school-outline" size={40} color="#E5E7EB" />
            <Text style={{ color: '#9CA3AF', marginTop: 8 }}>No training modules defined</Text>
            <Text style={{ color: '#D1D5DB', fontSize: 11, marginTop: 4 }}>Training data will appear here once configured</Text>
          </View>
        )}
      </SectionCard>

      {/* Training hours */}
      <SectionCard title="Training Hours This Year">
        {(deptStats||[]).slice(0, 5).map(([dept, stats]) => {
          const hours = Math.round(stats.count * (15 + Math.random() * 20));
          return (
            <View key={dept} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
              <Text style={{ flex: 1, fontSize: 12, color: '#374151', fontWeight: '600' }}>{dept}</Text>
              <Text style={{ fontSize: 11, color: '#6B7280', marginRight: 10 }}>{hours}h</Text>
              <View style={{ width: 80, height: 6, backgroundColor: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
                <View style={{ width: `${Math.min(hours / 40 * 100, 100)}%`, height: '100%', backgroundColor: DEPT_COLORS[dept] || '#0e4e96', borderRadius: 3 }} />
              </View>
            </View>
          );
        })}
      </SectionCard>
    </ScrollView>
  );

  // ── Tab: ANALYTICS ────────────────────────────────────────────────────────
  const renderAnalyticsTab = () => (
    <ScrollView style={sty.tabScroll} showsVerticalScrollIndicator={false} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#dc2626']} />}>
      {/* Time range selector */}
      <View style={{ flexDirection: 'row', backgroundColor: '#fff', borderRadius: 12, padding: 4, marginBottom: 16, gap: 2 }}>
        {['week', 'month', 'quarter', 'year'].map(r => (
          <TouchableOpacity key={r} style={{ flex: 1, paddingVertical: 8, borderRadius: 10, backgroundColor: timeRange === r ? '#0e4e96' : 'transparent', alignItems: 'center' }}
            onPress={() => setTimeRange(r)}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: timeRange === r ? '#fff' : '#6B7280' }}>{r.charAt(0).toUpperCase() + r.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Key KPIs */}
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <StatCard title="Headcount Growth"    value={`+${overview.newHires}`}        icon="trending-up"   color="#10b981" subtitle={timeRange} trend={15.7} />
        <StatCard title="Turnover Rate"       value={`${overview.turnoverRate}%`}     icon="trending-down" color="#EF4444" subtitle="Attrition"  trend={-2.1} />
        <StatCard title="Avg Salary Growth"   value="5.2%"                           icon="cash-plus"     color="#F59E0B" subtitle="Year on year" trend={5.2} />
        <StatCard title="eNPS Score"          value="42"                             icon="emoticon-happy" color="#8B5CF6" subtitle="Employee NPS" trend={8.3} />
      </View>

      {/* Department size chart */}
      <SectionCard title="Department Size">
        {(deptStats||[]).length > 0 ? (
          <BarChart data={(deptStats||[]).map(([name, s]) => ({ name, count: s.count }))} valueKey="count" labelKey="name" />
        ) : (
          <Text style={{ color: '#9CA3AF', textAlign: 'center', paddingVertical: 20 }}>No department data</Text>
        )}
      </SectionCard>

      {/* Salary distribution */}
      <SectionCard title="Salary Distribution">
        {[
          { range: '<$40k',      count: (employees||[]).filter(e => (e.salary || 0) < 40000).length,                       color: '#94A3B8' },
          { range: '$40k–$60k',  count: (employees||[]).filter(e => (e.salary || 0) >= 40000 && (e.salary||0) < 60000).length, color: '#3B82F6' },
          { range: '$60k–$80k',  count: (employees||[]).filter(e => (e.salary || 0) >= 60000 && (e.salary||0) < 80000).length, color: '#10B981' },
          { range: '$80k–$100k', count: (employees||[]).filter(e => (e.salary || 0) >= 80000 && (e.salary||0) < 100000).length, color: '#F59E0B' },
          { range: '>$100k',     count: (employees||[]).filter(e => (e.salary || 0) >= 100000).length,                     color: '#8B5CF6' },
        ].map((b, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
            <Text style={{ width: 80, fontSize: 11, color: '#6B7280', fontWeight: '600' }}>{b.range}</Text>
            <View style={{ flex: 1, height: 8, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden', marginHorizontal: 8 }}>
              <View style={{ width: (employees||[]).length ? `${pct(b.count, (employees||[]).length)}%` : '0%', height: '100%', backgroundColor: b.color, borderRadius: 4 }} />
            </View>
            <Text style={{ width: 28, fontSize: 11, fontWeight: '700', color: b.color, textAlign: 'right' }}>{b.count}</Text>
          </View>
        ))}
      </SectionCard>

      {/* Attendance by day */}
      <SectionCard title="Weekly Attendance Pattern">
        <BarChart
          data={[
            { day: 'Mon', rate: 96.2 }, { day: 'Tue', rate: 97.8 },
            { day: 'Wed', rate: 98.1 }, { day: 'Thu', rate: 97.5 },
            { day: 'Fri', rate: 94.3 },
          ]}
          valueKey="rate" labelKey="day" color="#06b6d4"
        />
      </SectionCard>

      {/* Status breakdown */}
      <SectionCard title="Workforce Status">
        {[
          { status: 'Active',    count: overview.activeEmployees, color: '#10b981' },
          { status: 'On Leave',  count: overview.onLeave,         color: '#F59E0B' },
          { status: 'Probation', count: (employees||[]).filter(e => e.status === 'Probation').length, color: '#EF4444' },
          { status: 'Inactive',  count: (employees||[]).filter(e => e.status === 'Inactive').length,  color: '#9CA3AF' },
        ].map((s, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: s.color, marginRight: 10 }} />
            <Text style={{ flex: 1, fontSize: 13, color: '#374151', fontWeight: '600' }}>{s.status}</Text>
            <Text style={{ fontSize: 12, color: '#6B7280', marginRight: 10 }}>{s.count} ({pct(s.count, overview.totalEmployees || 1)}%)</Text>
            <View style={{ width: 60, height: 6, backgroundColor: '#F1F5F9', borderRadius: 3, overflow: 'hidden' }}>
              <View style={{ width: `${pct(s.count, overview.totalEmployees || 1)}%`, height: '100%', backgroundColor: s.color, borderRadius: 3 }} />
            </View>
          </View>
        ))}
      </SectionCard>

      {/* Export */}
      <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 14, padding: 15, flexDirection: 'row', justifyContent: 'center', gap: 8, alignItems: 'center', marginBottom: 80 }} onPress={exportReport}>
        <MaterialCommunityIcons name="file-chart" size={20} color="#fff" />
        <Text style={{ color: '#fff', fontWeight: '900', fontSize: 15 }}>Export Full HR Report</Text>
      </TouchableOpacity>
    </ScrollView>
  );

  // ── MODALS ────────────────────────────────────────────────────────────────

  // Field helper used inside modals
  const Field = ({ label, value, onChange, placeholder, kb, multiline, required }) => (
    <View style={{ marginBottom: 14 }}>
      <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{label}{required ? ' *' : ''}</Text>
      <TextInput style={{ backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#111827', ...(multiline ? { minHeight: 80, textAlignVertical: 'top' } : {}) }}
        value={value || ''} onChangeText={onChange} placeholder={placeholder || ''} placeholderTextColor="#D1D5DB"
        keyboardType={kb || 'default'} multiline={!!multiline} />
    </View>
  );

  const ChipPicker = ({ label, value, onChange, options, color = '#0e4e96' }) => (
    <View style={{ marginBottom: 14 }}>
      <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>{label}</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 6 }}>
        {options.map(o => (
          <TouchableOpacity key={o} onPress={() => onChange(o)} style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 12, backgroundColor: value === o ? color : '#F3F4F6', borderWidth: 1, borderColor: value === o ? color : '#E5E7EB' }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: value === o ? '#fff' : '#6B7280' }}>{o}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const ModalShell = ({ visible, title, onClose, onSave, saveLabel = 'Save', saveColor = '#0e4e96', children }) => (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
          <TouchableOpacity onPress={onClose} style={{ padding: 4 }}><MaterialCommunityIcons name="close" size={24} color="#6B7280" /></TouchableOpacity>
          <Text style={{ fontSize: 17, fontWeight: '800', color: '#111827' }}>{title}</Text>
          <TouchableOpacity onPress={onSave} disabled={saving} style={{ backgroundColor: saveColor, borderRadius: 10, paddingHorizontal: 16, paddingVertical: 8, opacity: saving ? 0.6 : 1 }}>
            {saving ? <ActivityIndicator size={14} color="#fff" /> : <Text style={{ color: '#fff', fontWeight: '800', fontSize: 14 }}>{saveLabel}</Text>}
          </TouchableOpacity>
        </View>
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 20, paddingBottom: 40 }} showsVerticalScrollIndicator={false}>
            {children}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </Modal>
  );

  // Add Employee modal
  // Add Employee → navigates to AddEmployeeScreen (no modal needed)
  const renderAddEmpModal = () => null;

  // Employee detail modal
  const renderEmpDetailModal = () => {
    const e = selectedEmp;
    if (!e) return null;
    return (
      <Modal visible={modal === 'emp_detail'} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setModal(null)}>
        <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
            <TouchableOpacity onPress={() => setModal(null)}><MaterialCommunityIcons name="close" size={24} color="#6B7280" /></TouchableOpacity>
            <Text style={{ fontSize: 17, fontWeight: '800', color: '#111827' }}>Employee Profile</Text>
            <TouchableOpacity onPress={() => { setModal(null); navigation?.navigate('EmployeeDetailsScreen', { employee: e, employeeId: e.id }); }}>
              <Text style={{ color: '#0e4e96', fontWeight: '700', fontSize: 14 }}>Full →</Text>
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 40 }}>
            {/* Header */}
            <View style={{ alignItems: 'center', marginBottom: 24 }}>
              <Avatar name={e.name} uri={e.avatar} size={80} />
              <Text style={{ fontSize: 20, fontWeight: '900', color: '#111827', marginTop: 12 }}>{e.name}</Text>
              <Text style={{ fontSize: 14, color: '#6B7280', marginTop: 3 }}>{e.position} · {e.department}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <View style={{ backgroundColor: (STATUS_COLORS[e.status] || '#9CA3AF') + '20', borderRadius: 10, paddingHorizontal: 10, paddingVertical: 4 }}>
                  <Text style={{ fontSize: 12, fontWeight: '700', color: STATUS_COLORS[e.status] || '#9CA3AF' }}>{e.status}</Text>
                </View>
                <View style={{ flexDirection: 'row' }}>
                  <Text>{[1,2,3,4,5].map(s => <MaterialCommunityIcons key={s} name={s <= e.performanceRating ? 'star' : 'star-outline'} size={16} color="#F59E0B" />)}</Text>
                </View>
              </View>
            </View>

            {/* Quick actions */}
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 20 }}>
              {[
                { icon:'email-outline', label:'Email', color:'#3B82F6', onPress:()=>Linking.openURL(`mailto:${e.email}`) },
                { icon:'phone-outline',  label:'Call',  color:'#10b981', onPress:()=>Linking.openURL(`tel:${e.phone}`) },
                { icon:'chart-line',     label:'Review', color:'#8B5CF6', onPress:()=>{setModal(null);setTimeout(()=>{setReviewForm(p=>({...p,employeeId:e.serverId||e.id,employeeName:e.name}));setModal('schedule_review');},300)} },
                ...(can('issue_warning') ? [{ icon:'alert-circle',   label:'Warning',color:'#EF4444', onPress:()=>{setModal(null);setTimeout(()=>{setWarningForm(p=>({...p,employeeId:e.serverId||e.id,employeeName:e.name}));setModal('issue_warning');},300)} }] : []),
              ].map(btn => (
                <TouchableOpacity key={btn.label} style={{ flex: 1, backgroundColor: btn.color + '15', borderRadius: 12, padding: 10, alignItems: 'center', borderWidth: 1, borderColor: btn.color + '25' }} onPress={btn.onPress}>
                  <MaterialCommunityIcons name={btn.icon} size={20} color={btn.color} />
                  <Text style={{ fontSize: 10, fontWeight: '700', color: btn.color, marginTop: 4 }}>{btn.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* Detail rows */}
            {[
              { section: 'Contact', rows: [['Email', e.email], ['Phone', e.phone]] },
              { section: 'Employment', rows: [['Employee ID', e.employeeId], ['Type', e.employeeType], ['Start Date', fmtDate(e.startDate)], ['Manager', e.manager], ['Location', e.location], ['Salary', fmtMoney(e.salary) + '/yr']] },
              { section: 'Performance & Attendance', rows: [['Last Review', fmtDate(e.lastReview) || 'Not reviewed'], ['Attendance', `${e.attendanceRate}%`], ['Leave Balance', `${e.leaveBalance} days`], ['Warnings', `${e.warningCount || 0}`]] },
            ].map(sec => (
              <View key={sec.section} style={sty.sectionCard}>
                <Text style={{ fontSize: 13, fontWeight: '900', color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>{sec.section}</Text>
                {(sec.rows||[]).filter(([, v]) => v).map(([l, v], i) => (
                  <View key={l} style={{ flexDirection: 'row', paddingVertical: 8, borderBottomWidth: i < (sec.rows||[]).length - 1 ? 1 : 0, borderBottomColor: '#F8FAFC' }}>
                    <Text style={{ width: 120, fontSize: 13, color: '#9CA3AF', fontWeight: '600' }}>{l}</Text>
                    <Text style={{ flex: 1, fontSize: 13, color: '#111827', fontWeight: '600' }}>{v}</Text>
                  </View>
                ))}
              </View>
            ))}

            {/* Skills */}
            {e.skills?.length > 0 && (
              <View style={sty.sectionCard}>
                <Text style={{ fontSize: 13, fontWeight: '900', color: '#374151', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Skills</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {e.skills.map((s, i) => (
                    <View key={i} style={{ backgroundColor: '#EFF6FF', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 5 }}>
                      <Text style={{ fontSize: 12, color: '#3B82F6', fontWeight: '600' }}>{s}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Navigate to full profile */}
            <TouchableOpacity style={{ backgroundColor: '#0e4e96', borderRadius: 14, padding: 14, flexDirection: 'row', justifyContent: 'center', gap: 8, alignItems: 'center', marginTop: 8 }}
              onPress={() => { setModal(null); navigation?.navigate('EmployeeDetailsScreen', { employee: e, employeeId: e.id }); }}>
              <MaterialCommunityIcons name="account-details" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '800', fontSize: 14 }}>Open Full Employee Profile</Text>
            </TouchableOpacity>
          </ScrollView>
        </SafeAreaView>
      </Modal>
    );
  };

  // Leave approval modal (for rejection with note)
  const renderLeaveApproveModal = () => (
    <Modal visible={modal === 'leave_approve'} transparent animationType="slide" onRequestClose={() => setModal(null)}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }}>
        <View style={{ backgroundColor: '#fff', borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: Platform.OS === 'ios' ? 40 : 24 }}>
          <Text style={{ fontSize: 18, fontWeight: '900', color: '#111827', marginBottom: 6 }}>Reject Leave Request</Text>
          {selectedLeave && <Text style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
            {selectedLeave.employee_name || selectedLeave.name} · {selectedLeave.leave_type || selectedLeave.type || 'Leave'} · {selectedLeave.days || 1} day(s)
          </Text>}
          <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 8 }}>REASON FOR REJECTION</Text>
          <TextInput style={{ backgroundColor: '#F9FAFB', borderWidth: 1.5, borderColor: '#E5E7EB', borderRadius: 12, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#111827', minHeight: 80, textAlignVertical: 'top', marginBottom: 20 }}
            value={leaveNote} onChangeText={setLeaveNote} placeholder="Explain why this leave is being rejected…" multiline />
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 12, padding: 14, alignItems: 'center' }} onPress={() => { setModal(null); setLeaveNote(''); }}>
              <Text style={{ color: '#6B7280', fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{ flex: 1, backgroundColor: '#EF4444', borderRadius: 12, padding: 14, alignItems: 'center' }} onPress={() => handleApproveLeave(false)}>
              <Text style={{ color: '#fff', fontWeight: '800' }}>Reject Request</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Process payroll confirmation modal
  const renderProcessPayrollModal = () => (
    <Modal visible={modal === 'process_payroll'} transparent animationType="slide" onRequestClose={() => setModal(null)}>
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: 20 }}>
        <View style={{ backgroundColor: '#fff', borderRadius: 24, padding: 24 }}>
          <View style={{ alignItems: 'center', marginBottom: 20 }}>
            <View style={{ width: 64, height: 64, borderRadius: 32, backgroundColor: '#ECFDF5', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
              <MaterialCommunityIcons name="cash-multiple" size={32} color="#10b981" />
            </View>
            <Text style={{ fontSize: 20, fontWeight: '900', color: '#111827' }}>Process Payroll</Text>
            <Text style={{ fontSize: 13, color: '#6B7280', marginTop: 6, textAlign: 'center', lineHeight: 20 }}>
              This will process payroll for {(employees||[]).filter(e => e.status === 'Active').length} active employees
            </Text>
          </View>
          {[
            ['Total Payroll', fmtMoney((employees||[]).filter(e => e.status === 'Active').reduce((s, e) => s + (parseInt(e.salary) || 0), 0))],
            ['Average Salary', fmtMoney(payrollData.averageSalary)],
            ['Active Employees', (employees||[]).filter(e => e.status === 'Active').length],
            ['Estimated Net', fmtMoney((employees||[]).filter(e => e.status === 'Active').reduce((s, e) => s + (parseInt(e.salary) || 0), 0) * 0.78)],
          ].map(([l, v]) => (
            <View key={l} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' }}>
              <Text style={{ fontSize: 13, color: '#6B7280' }}>{l}</Text>
              <Text style={{ fontSize: 13, fontWeight: '700', color: '#111827' }}>{v}</Text>
            </View>
          ))}
          <View style={{ flexDirection: 'row', gap: 10, marginTop: 20 }}>
            <TouchableOpacity style={{ flex: 1, backgroundColor: '#F3F4F6', borderRadius: 12, padding: 14, alignItems: 'center' }} onPress={() => setModal(null)}>
              <Text style={{ color: '#6B7280', fontWeight: '700' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{ flex: 2, backgroundColor: '#10b981', borderRadius: 12, padding: 14, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 6 }} onPress={handleProcessPayroll} disabled={saving}>
              {saving ? <ActivityIndicator size={16} color="#fff" /> : <MaterialCommunityIcons name="cash-multiple" size={16} color="#fff" />}
              <Text style={{ color: '#fff', fontWeight: '800' }}>Confirm & Process</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Schedule review modal
  const renderScheduleReviewModal = () => (
    <ModalShell visible={modal === 'schedule_review'} title="Schedule Performance Review" onClose={() => setModal(null)} onSave={handleScheduleReview} saveLabel="Schedule" saveColor="#8b5cf6">
      <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 8 }}>SELECT EMPLOYEE *</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, marginBottom: 16 }}>
        {(employees||[]).slice(0, 15).map(e => (
          <TouchableOpacity key={e.id} onPress={() => setReviewForm(p => ({ ...p, employeeId: e.id, employeeName: e.name }))}
            style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: reviewForm.employeeName === e.name ? '#8b5cf6' : '#F3F4F6', borderWidth: 1, borderColor: reviewForm.employeeName === e.name ? '#8b5cf6' : '#E5E7EB' }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: reviewForm.employeeName === e.name ? '#fff' : '#374151' }}>{e.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <ChipPicker label="Review Type" value={reviewForm.type} onChange={v => setReviewForm(p => ({ ...p, type: v }))} options={['Annual', 'Mid-Year', 'Probation', 'Quarterly', 'Special']} color="#8b5cf6" />
      <Field label="Review Date" value={reviewForm.reviewDate} onChange={v => setReviewForm(p => ({ ...p, reviewDate: v }))} placeholder="YYYY-MM-DD" />
      <Field label="Reviewer / Manager" value={reviewForm.reviewer} onChange={v => setReviewForm(p => ({ ...p, reviewer: v }))} placeholder="Reviewer name" />
      <Field label="Review Period" value={reviewForm.period} onChange={v => setReviewForm(p => ({ ...p, period: v }))} placeholder="e.g. Q1 2026, Jan–Jun 2026" />
      <Field label="Notes / Preparation" value={reviewForm.notes} onChange={v => setReviewForm(p => ({ ...p, notes: v }))} placeholder="Topics to cover, goals to review…" multiline />
    </ModalShell>
  );

  // Post job modal
  const renderPostJobModal = () => (
    <ModalShell visible={modal === 'post_job'} title="Post Job Opening" onClose={() => setModal(null)} onSave={handlePostJob} saveLabel="Post Job" saveColor="#06b6d4">
      <Field label="Job Title *" value={jobForm.title} onChange={v => setJobForm(p => ({ ...p, title: v }))} placeholder="Senior Software Engineer" required />
      <ChipPicker label="Department" value={jobForm.department} onChange={v => setJobForm(p => ({ ...p, department: v }))} options={departments.slice(1)} color="#0e4e96" />
      <ChipPicker label="Employment Type" value={jobForm.type} onChange={v => setJobForm(p => ({ ...p, type: v }))} options={empTypes.slice(1)} color="#8b5cf6" />
      <ChipPicker label="Work Location" value={jobForm.location} onChange={v => setJobForm(p => ({ ...p, location: v }))} options={empLocations.slice(1)} color="#06b6d4" />
      <Field label="Salary Range" value={jobForm.salary} onChange={v => setJobForm(p => ({ ...p, salary: v }))} placeholder="e.g. $60,000–$80,000" />
      <Field label="Job Description *" value={jobForm.description} onChange={v => setJobForm(p => ({ ...p, description: v }))} placeholder="What will this person do day-to-day?" multiline required />
      <Field label="Requirements" value={jobForm.requirements} onChange={v => setJobForm(p => ({ ...p, requirements: v }))} placeholder="Must-have skills and experience…" multiline />
      <TouchableOpacity style={{ backgroundColor: '#F0FDFE', borderRadius: 12, padding: 14, flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderColor: '#06b6d420', marginTop: 4 }}
        onPress={() => { setModal(null); navigation?.navigate('PostJobScreen', { companyId }); }}>
        <MaterialCommunityIcons name="arrow-right" size={18} color="#06b6d4" />
        <Text style={{ fontSize: 13, color: '#0e4e96', fontWeight: '700' }}>Open Full Job Posting Screen</Text>
      </TouchableOpacity>
    </ModalShell>
  );

  // Issue warning modal
  const renderIssueWarningModal = () => (
    <ModalShell visible={modal === 'issue_warning'} title="Issue Warning Letter" onClose={() => setModal(null)} onSave={handleIssueWarning} saveLabel="Issue" saveColor="#dc2626">
      <Text style={{ fontSize: 11, fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: 8 }}>SELECT EMPLOYEE *</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, marginBottom: 16 }}>
        {(employees||[]).slice(0, 15).map(e => (
          <TouchableOpacity key={e.id} onPress={() => setWarningForm(p => ({ ...p, employeeId: e.id, employeeName: e.name }))}
            style={{ paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, backgroundColor: warningForm.employeeName === e.name ? '#dc2626' : '#F3F4F6', borderWidth: 1, borderColor: warningForm.employeeName === e.name ? '#dc2626' : '#E5E7EB' }}>
            <Text style={{ fontSize: 12, fontWeight: '600', color: warningForm.employeeName === e.name ? '#fff' : '#374151' }}>{e.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <ChipPicker label="Warning Type" value={warningForm.type} onChange={v => setWarningForm(p => ({ ...p, type: v }))} options={warnTypes} color="#dc2626" />
      <ChipPicker label="Reason" value={warningForm.reason} onChange={v => setWarningForm(p => ({ ...p, reason: v }))} options={warnReasons} color="#F59E0B" />
      <ChipPicker label="Severity" value={warningForm.severity} onChange={v => setWarningForm(p => ({ ...p, severity: v }))} options={warnSevs} color="#EF4444" />
      <Field label="Issued Date" value={warningForm.issuedDate} onChange={v => setWarningForm(p => ({ ...p, issuedDate: v }))} placeholder="YYYY-MM-DD" />
      <Field label="Description *" value={warningForm.description} onChange={v => setWarningForm(p => ({ ...p, description: v }))} placeholder="Describe the incident or reason for this warning…" multiline required />
      <Field label="Notes" value={warningForm.notes} onChange={v => setWarningForm(p => ({ ...p, notes: v }))} placeholder="Additional notes or follow-up actions…" multiline />
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 12, marginTop: 4 }}>
        <Text style={{ fontSize: 14, color: '#374151', fontWeight: '600' }}>Acknowledgment Required</Text>
        <Switch value={warningForm.acknowledgmentRequired} onValueChange={v => setWarningForm(p => ({ ...p, acknowledgmentRequired: v }))} trackColor={{ false: '#D1D5DB', true: '#dc2626' }} thumbColor="#fff" />
      </View>
    </ModalShell>
  );

  // Notification centre modal
  // ── Create Onboarding Modal ──────────────────────────────────────────────
  const renderCreateOnboardModal = () => (
    <ModalShell visible={modal === 'create_onboard'} title="Create Onboarding Plan" onClose={()=>setModal(null)} onSave={handleCreateOnboarding} saveLabel="Create" saveColor="#7C3AED">
      <Text style={{ fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8 }}>SELECT EMPLOYEE *</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:8,marginBottom:16 }}>
        {(employees||[]).slice(0,15).map(e=>(
          <TouchableOpacity key={e.id} onPress={()=>setOnboardForm(p=>({...p,employeeId:e.id,employeeName:e.name,department:e.department,manager:e.manager||''}))}
            style={{ paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:onboardForm.employeeName===e.name?'#7C3AED':'#F3F4F6',borderWidth:1,borderColor:onboardForm.employeeName===e.name?'#7C3AED':'#E5E7EB' }}>
            <Text style={{ fontSize:12,fontWeight:'600',color:onboardForm.employeeName===e.name?'#fff':'#374151' }}>{e.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <Field label="Start Date" value={onboardForm.startDate} onChange={v=>setOnboardForm(p=>({...p,startDate:v}))} placeholder="YYYY-MM-DD"/>
      <Field label="Assigned Manager" value={onboardForm.manager} onChange={v=>setOnboardForm(p=>({...p,manager:v}))} placeholder="Manager name"/>
      <Field label="Notes" value={onboardForm.notes} onChange={v=>setOnboardForm(p=>({...p,notes:v}))} placeholder="Special instructions or context…" multiline/>
    </ModalShell>
  );

  const renderCreateExitModal = () => (
    <ModalShell visible={modal === 'create_exit'} title="Record Exit Interview" onClose={()=>setModal(null)} onSave={handleCreateExitInterview} saveLabel="Save" saveColor="#374151">
      <Text style={{ fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8 }}>SELECT EMPLOYEE *</Text>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap:8,marginBottom:16 }}>
        {(employees||[]).slice(0,15).map(e=>(
          <TouchableOpacity key={e.id} onPress={()=>setExitForm(p=>({...p,employeeId:e.id,employeeName:e.name}))}
            style={{ paddingHorizontal:12,paddingVertical:8,borderRadius:12,backgroundColor:exitForm.employeeName===e.name?'#374151':'#F3F4F6',borderWidth:1,borderColor:exitForm.employeeName===e.name?'#374151':'#E5E7EB' }}>
            <Text style={{ fontSize:12,fontWeight:'600',color:exitForm.employeeName===e.name?'#fff':'#374151' }}>{e.name}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <ChipPicker label="Reason for Leaving" value={exitForm.reason} onChange={v=>setExitForm(p=>({...p,reason:v}))} options={['Resignation','Redundancy','Retirement','Contract End','Dismissal','Mutual Agreement','Other']} color="#374151"/>
      <Field label="Last Working Date" value={exitForm.lastDate} onChange={v=>setExitForm(p=>({...p,lastDate:v}))} placeholder="YYYY-MM-DD"/>
      <Text style={{ fontSize:11,fontWeight:'800',color:'#9CA3AF',textTransform:'uppercase',marginBottom:8 }}>SATISFACTION (1-5)</Text>
      <View style={{ flexDirection:'row',justifyContent:'center',gap:12,marginBottom:16 }}>
        {['1','2','3','4','5'].map(r=>(
          <TouchableOpacity key={r} onPress={()=>setExitForm(p=>({...p,rating:r}))}
            style={{ width:48,height:48,borderRadius:24,backgroundColor:exitForm.rating===r?'#374151':'#F3F4F6',borderWidth:2,borderColor:exitForm.rating===r?'#374151':'#E5E7EB',alignItems:'center',justifyContent:'center' }}>
            <Text style={{ fontSize:16,fontWeight:'900',color:exitForm.rating===r?'#fff':'#6B7280' }}>{r}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <Field label="Feedback / Comments" value={exitForm.feedback} onChange={v=>setExitForm(p=>({...p,feedback:v}))} placeholder="What could we have done better?" multiline/>
      <Field label="HR Notes" value={exitForm.notes} onChange={v=>setExitForm(p=>({...p,notes:v}))} placeholder="Internal HR notes…" multiline/>
      <View style={{ flexDirection:'row',alignItems:'center',justifyContent:'space-between',paddingVertical:12 }}>
        <Text style={{ fontSize:14,color:'#374151',fontWeight:'600' }}>Eligible for Rehire</Text>
        <Switch value={exitForm.rehirable} onValueChange={v=>setExitForm(p=>({...p,rehirable:v}))} trackColor={{ false:'#D1D5DB',true:'#059669' }} thumbColor="#fff"/>
      </View>
    </ModalShell>
  );

  const renderCreateBenefitModal = () => (
    <ModalShell visible={modal === 'create_benefit'} title="Add Benefit Plan" onClose={()=>setModal(null)} onSave={handleCreateBenefit} saveLabel="Create" saveColor="#059669">
      <Field label="Plan Name *" value={benefitForm.planName} onChange={v=>setBenefitForm(p=>({...p,planName:v}))} placeholder="e.g. Health Insurance Gold Plan" required/>
      <ChipPicker label="Type" value={benefitForm.type} onChange={v=>setBenefitForm(p=>({...p,type:v}))} options={['Health','Dental','Vision','Pension','Life','Wellness','Family','Transport','Other']} color="#059669"/>
      <Field label="Provider" value={benefitForm.provider} onChange={v=>setBenefitForm(p=>({...p,provider:v}))} placeholder="e.g. Aetna, Fidelity"/>
      <Field label="Monthly Cost per Employee ($)" value={benefitForm.cost} onChange={v=>setBenefitForm(p=>({...p,cost:v}))} placeholder="e.g. 450" kb="numeric"/>
      <Field label="Coverage Details" value={benefitForm.coverage} onChange={v=>setBenefitForm(p=>({...p,coverage:v}))} placeholder="e.g. Individual + Family"/>
      <ChipPicker label="Eligibility" value={benefitForm.eligibility} onChange={v=>setBenefitForm(p=>({...p,eligibility:v}))} options={['All','Full-time','Part-time','Contract']} color="#0e4e96"/>
      <Field label="Description" value={benefitForm.description} onChange={v=>setBenefitForm(p=>({...p,description:v}))} placeholder="Additional details…" multiline/>
    </ModalShell>
  );

  const renderCreatePolicyModal = () => (
    <ModalShell visible={modal === 'create_policy'} title="New HR Policy" onClose={()=>setModal(null)} onSave={handleCreatePolicy} saveLabel="Create" saveColor="#0891B2">
      <Field label="Policy Title *" value={policyForm.title} onChange={v=>setPolicyForm(p=>({...p,title:v}))} placeholder="e.g. Code of Conduct Policy" required/>
      <ChipPicker label="Category" value={policyForm.category} onChange={v=>setPolicyForm(p=>({...p,category:v}))} options={['HR','Compliance','Operations','IT','Finance','Safety','Legal']} color="#0891B2"/>
      <Field label="Description" value={policyForm.description} onChange={v=>setPolicyForm(p=>({...p,description:v}))} placeholder="Policy summary and scope…" multiline/>
      <Field label="Effective Date" value={policyForm.effectiveDate} onChange={v=>setPolicyForm(p=>({...p,effectiveDate:v}))} placeholder="YYYY-MM-DD"/>
      <Field label="Review Date" value={policyForm.reviewDate} onChange={v=>setPolicyForm(p=>({...p,reviewDate:v}))} placeholder="YYYY-MM-DD"/>
      <ChipPicker label="Status" value={policyForm.status} onChange={v=>setPolicyForm(p=>({...p,status:v}))} options={['Active','Draft','Under Review','Archived']} color="#374151"/>
    </ModalShell>
  );

  const renderBulkModal = () => (
    <Modal visible={showBulkModal} transparent animationType="slide" onRequestClose={()=>setShowBulkModal(false)}>
      <View style={{ flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'flex-end' }}>
        <View style={{ backgroundColor:'#fff',borderTopLeftRadius:24,borderTopRightRadius:24,padding:24,paddingBottom:Platform.OS==='ios'?40:24 }}>
          <Text style={{ fontSize:18,fontWeight:'900',color:'#111827',marginBottom:6 }}>Bulk Actions</Text>
          <Text style={{ fontSize:13,color:'#6B7280',marginBottom:20 }}>{selectedBulk.length} employees selected</Text>
          {[
            { label:'Set Active',     icon:'account-check',   color:'#10B981', action:()=>handleBulkAction('status','Active') },
            { label:'Set On Leave',   icon:'account-clock',   color:'#F59E0B', action:()=>handleBulkAction('status','On Leave') },
            { label:'Set Inactive',   icon:'account-off',     color:'#9CA3AF', action:()=>handleBulkAction('status','Inactive') },
            { label:'Set Remote',     icon:'home-account',    color:'#8B5CF6', action:()=>handleBulkAction('location','Remote') },
            { label:'Set Office',     icon:'office-building', color:'#0e4e96', action:()=>handleBulkAction('location','Office') },
            { label:'Export Selected',icon:'export',          color:'#374151', action:async()=>{
              const sel=(employees||[]).filter(e=>selectedBulk.includes(e.id));
              await Share.share({message:sel.map(e=>`${e.name}, ${e.position}, ${e.department}, ${e.status}, ${fmtMoney(e.salary)}`).join('\n'),title:'Selected Employees'});
              setShowBulkModal(false);
            }},
          ].map((btn,i)=>(
            <TouchableOpacity key={i} style={{ flexDirection:'row',alignItems:'center',paddingVertical:13,borderBottomWidth:i<5?1:0,borderBottomColor:'#F8FAFC',gap:12 }}
              onPress={()=>{btn.action();setShowBulkModal(false);}}>
              <View style={{ width:40,height:40,borderRadius:20,backgroundColor:btn.color+'15',alignItems:'center',justifyContent:'center' }}>
                <MaterialCommunityIcons name={btn.icon} size={20} color={btn.color}/>
              </View>
              <Text style={{ fontSize:14,fontWeight:'600',color:'#374151' }}>{btn.label}</Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity style={{ backgroundColor:'#F3F4F6',borderRadius:12,padding:14,alignItems:'center',marginTop:12 }} onPress={()=>{setShowBulkModal(false);setSelectedBulk([]);setBulkMode(false);}}>
            <Text style={{ color:'#6B7280',fontWeight:'700' }}>Cancel & Deselect All</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderNotifModal = () => (
    <Modal visible={modal === 'notification_center'} animationType="slide" presentationStyle="pageSheet" onRequestClose={() => setModal(null)}>
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8FAFC' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
          <TouchableOpacity onPress={() => setModal(null)}><MaterialCommunityIcons name="close" size={24} color="#6B7280" /></TouchableOpacity>
          <Text style={{ fontSize: 17, fontWeight: '800', color: '#111827' }}>Notifications</Text>
          <TouchableOpacity onPress={markAllRead}><Text style={{ color: '#0e4e96', fontWeight: '700', fontSize: 13 }}>Mark all read</Text></TouchableOpacity>
        </View>
        <FlatList data={notifications} keyExtractor={(n, i) => String(n.id || i)}
          contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
          ListEmptyComponent={<View style={{ alignItems: 'center', paddingVertical: 60 }}><MaterialCommunityIcons name="bell-check" size={56} color="#E5E7EB" /><Text style={{ color: '#9CA3AF', marginTop: 12 }}>All caught up!</Text></View>}
          renderItem={({ item: n }) => {
            const typeColor = { urgent: '#dc2626', warning: '#f59e0b', success: '#10b981', info: '#0e4e96' }[n.type] || '#6B7280';
            return (
              <TouchableOpacity style={{ backgroundColor: n.read ? '#fff' : '#EFF6FF', borderRadius: 14, padding: 16, marginBottom: 10, borderLeftWidth: 4, borderLeftColor: n.read ? 'transparent' : typeColor }}
                onPress={() => setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x))}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
                  <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: typeColor + '15', alignItems: 'center', justifyContent: 'center' }}>
                    <MaterialCommunityIcons name={n.type === 'urgent' ? 'alert-circle' : n.type === 'success' ? 'check-circle' : n.type === 'warning' ? 'alert' : 'information'} size={18} color={typeColor} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Text style={{ fontSize: 14, fontWeight: '700', color: '#111827', flex: 1 }}>{n.title}</Text>
                      {!n.read && <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#0e4e96' }} />}
                    </View>
                    <Text style={{ fontSize: 12, color: '#6B7280', marginTop: 4, lineHeight: 18 }}>{n.message}</Text>
                    <Text style={{ fontSize: 10, color: '#9CA3AF', marginTop: 5 }}>{n.timestamp ? timeAgo(n.timestamp) : ''}</Text>
                  </View>
                </View>
              </TouchableOpacity>
            );
          }}
        />
      </SafeAreaView>
    </Modal>
  );


  // ── Password Gate Screen ──
  if (screenPasswordChecked && screenHasPassword && !screenPasswordVerified) {
    const isLocked = screenLockoutUntil && Date.now() < screenLockoutUntil;
    const lockMins = isLocked ? Math.ceil((screenLockoutUntil - Date.now()) / 60000) : 0;

    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={{ paddingVertical: 24, paddingHorizontal: 20, alignItems: 'center' }}>
          <TouchableOpacity onPress={() => navigation?.goBack?.()} style={{ position: 'absolute', left: 16, top: 24 }}>
            <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
          </TouchableOpacity>
          <View style={{ width: 60, height: 60, borderRadius: 30, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
            <MaterialCommunityIcons name="lock" size={30} color="#fff" />
          </View>
          <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#fff' }}>Password Required</Text>
          <Text style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 4 }}>
            HR Dashboard is protected by admin password
          </Text>
        </LinearGradient>

        <View style={{ padding: 24 }}>
          {isLocked ? (
            <View style={{ alignItems: 'center', paddingVertical: 30 }}>
              <MaterialCommunityIcons name="clock-outline" size={48} color="#EF4444" />
              <Text style={{ fontSize: 17, fontWeight: 'bold', color: '#EF4444', marginTop: 12 }}>Locked</Text>
              <Text style={{ fontSize: 14, color: '#64748B', marginTop: 6 }}>Try again in {lockMins} min{lockMins > 1 ? 's' : ''}.</Text>
              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ marginTop: 20, backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 13, paddingHorizontal: 28 }}>
                <Text style={{ color: '#fff', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <>
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, borderWidth: 1, borderColor: screenPasswordError ? '#EF4444' : '#E2E8F0', paddingHorizontal: 14, marginBottom: 12 }}>
                <MaterialCommunityIcons name="lock" size={18} color={screenPasswordError ? '#EF4444' : '#94A3B8'} />
                <TextInput
                  style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 10, fontSize: 16, color: '#222' }}
                  placeholder="Enter admin password"
                  placeholderTextColor="#94A3B8"
                  value={screenPassword}
                  onChangeText={(t) => { setScreenPassword(t); setScreenPasswordError(''); }}
                  secureTextEntry={!showScreenPassword}
                  autoFocus
                  onSubmitEditing={verifyScreenPassword}
                  returnKeyType="go"
                />
                <TouchableOpacity onPress={() => setShowScreenPassword(v => !v)}>
                  <MaterialCommunityIcons name={showScreenPassword ? 'eye-off' : 'eye'} size={20} color="#94A3B8" />
                </TouchableOpacity>
              </View>

              {screenPasswordError ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12, backgroundColor: '#FEF2F2', padding: 10, borderRadius: 10 }}>
                  <MaterialCommunityIcons name="alert-circle-outline" size={16} color="#EF4444" />
                  <Text style={{ fontSize: 12, color: '#EF4444', flex: 1 }}>{screenPasswordError}</Text>
                </View>
              ) : null}

              {screenPasswordAttempts > 0 && (
                <Text style={{ fontSize: 11, color: '#94A3B8', textAlign: 'center', marginBottom: 10 }}>
                  {SCREEN_MAX_ATTEMPTS - screenPasswordAttempts} attempt{SCREEN_MAX_ATTEMPTS - screenPasswordAttempts > 1 ? 's' : ''} remaining
                </Text>
              )}

              <TouchableOpacity onPress={verifyScreenPassword}
                style={{ backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 15, alignItems: 'center', marginBottom: 10 }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 16 }}>Unlock</Text>
              </TouchableOpacity>

              <TouchableOpacity onPress={() => navigation?.goBack?.()}
                style={{ borderRadius: 12, paddingVertical: 13, alignItems: 'center', borderWidth: 1, borderColor: '#E2E8F0' }}>
                <Text style={{ color: '#64748B', fontWeight: '600' }}>Go Back</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </SafeAreaView>
    );
  }

  // ── Auth Gate ──
  if (authChecked && !isAdminAuthorized) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: '#F8F9FA' }}>
        <StatusBar barStyle="dark-content" />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 30 }}>
          <View style={{ width: 80, height: 80, borderRadius: 40, backgroundColor: '#EF444420', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
            <MaterialCommunityIcons name="lock" size={40} color="#EF4444" />
          </View>
          <Text style={{ fontSize: 22, fontWeight: 'bold', color: '#1a1a2e', marginBottom: 8 }}>Access Restricted</Text>
          <Text style={{ fontSize: 15, color: '#64748B', textAlign: 'center', lineHeight: 22, marginBottom: 6 }}>
            HR Dashboard is available to authorized administrators, HR managers, and business owners only.
          </Text>
          {currentUser ? (
            <Text style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', marginBottom: 20 }}>
              Signed in as: {currentUser.full_name || currentUser.name || currentUser.email || 'Unknown'}{String.fromCharCode(10)}
              Role: {currentUser.user_role || currentUser.role || 'Not assigned'}
            </Text>
          ) : (
            <Text style={{ fontSize: 13, color: '#94A3B8', textAlign: 'center', marginBottom: 20 }}>
              No user session found. Please sign in first.
            </Text>
          )}
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <TouchableOpacity onPress={() => navigation?.goBack()}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#0e4e96', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8 }}>
              <MaterialCommunityIcons name="arrow-left" size={18} color="#fff" />
              <Text style={{ color: '#fff', fontWeight: '600', fontSize: 15 }}>Go Back</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setAuthChecked(false); checkUserAuthorization(companyId); }}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0F4FF', borderRadius: 12, paddingVertical: 14, paddingHorizontal: 24, gap: 8, borderWidth: 1, borderColor: '#0e4e96' }}>
              <MaterialCommunityIcons name="refresh" size={18} color="#0e4e96" />
              <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 15 }}>Retry</Text>
            </TouchableOpacity>
          </View>
        </View>
      </SafeAreaView>
    );
  }

  // ── Main return ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={sty.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <LinearGradient colors={['#0e4e96', '#1e6bc2']} style={sty.header}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={{ padding: 6 }}>
          <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            {companyLogo ? (
              <TouchableOpacity onPress={() => navigation?.navigate('CompanyProfileScreen', { companyId })}>
                <Image source={{ uri: companyLogo || undefined }} style={{ width: 32, height: 32, borderRadius: 8 }} resizeMode="cover" />
              </TouchableOpacity>
            ) : null}
            <View>
              <Text style={{ fontSize: 17, fontWeight: '900', color: '#fff' }}>HR Dashboard</Text>
              {companyName ? <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{companyName}</Text> : null}
            </View>
          </View>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
          {isAdminAuthorized && (
            <TouchableOpacity
              style={{ padding: 6 }}
              onPress={() => navigation?.navigate('AdminHRManagementScreen', {
                companyProfile: { name: companyName, logo: companyLogo, company_name: companyName },
                companyId,
                user: currentUser,
              })}
            >
              <MaterialCommunityIcons name="shield-crown" size={22} color="#FFD700" />
            </TouchableOpacity>
          )}
          <TouchableOpacity style={{ padding: 6, position: 'relative' }} onPress={() => can('view_notifications') ? setModal('notification_center') : Alert.alert('Restricted','Notification access is restricted.')}>
            <MaterialCommunityIcons name="bell-outline" size={24} color="#fff" />
          {unreadNotifs > 0 && (
            <View style={{ position: 'absolute', top: 2, right: 2, backgroundColor: '#EF4444', borderRadius: 10, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: '#fff', fontSize: 10, fontWeight: '900' }}>{unreadNotifs}</Text>
            </View>
          )}
        </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Tab bar */}
      <View style={{ backgroundColor: '#fff', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, elevation: 2 }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 4 }}>
          {tabs.map(tab => (
            <TouchableOpacity key={tab.id} onPress={() => setActiveTab(tab.id)}
              style={{ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 14, paddingVertical: 13, borderBottomWidth: 2.5, borderBottomColor: activeTab === tab.id ? tab.color : 'transparent' }}>
              <MaterialCommunityIcons name={tab.icon} size={16} color={activeTab === tab.id ? tab.color : '#94A3B8'} />
              <Text style={{ fontSize: 12, fontWeight: activeTab === tab.id ? '800' : '500', color: activeTab === tab.id ? tab.color : '#94A3B8' }}>{tab.title}</Text>
              {/* Badge for pending actions */}
              {tab.id === 'leave' && overview.pendingLeave > 0 && (
                <View style={{ backgroundColor: '#F59E0B', borderRadius: 9, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 }}>
                  <Text style={{ color: '#fff', fontSize: 9, fontWeight: '900' }}>{overview.pendingLeave}</Text>
                </View>
              )}
              {tab.id === 'performance' && performanceData.reviewsPending > 0 && (
                <View style={{ backgroundColor: '#8b5cf6', borderRadius: 9, minWidth: 18, height: 18, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 4 }}>
                  <Text style={{ color: '#fff', fontSize: 9, fontWeight: '900' }}>{performanceData.reviewsPending}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      {loading ? (
        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14 }}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={{ fontSize: 15, color: '#6B7280' }}>Loading HR data…</Text>
        </View>
      ) : (
        <Animated.View style={{ flex: 1, opacity: fadeAnim }}>
          {activeTab === 'overview'    && renderOverviewTab()}
          {activeTab === 'employees'   && renderEmployeesTab()}
          {activeTab === 'payroll'     && renderPayrollTab()}
          {activeTab === 'performance' && renderPerformanceTab()}
          {activeTab === 'leave'       && renderLeaveTab()}
          {activeTab === 'recruitment' && renderRecruitmentTab()}
          {activeTab === 'training'    && renderTrainingTab()}
          {activeTab === 'analytics'   && renderAnalyticsTab()}
          {activeTab === 'onboarding'  && renderOnboardingTab()}
          {activeTab === 'disciplinary'&& renderDisciplinaryTab()}
          {activeTab === 'compliance'  && renderComplianceTab()}
          {activeTab === 'reports'     && renderReportsTab()}
          {activeTab === 'benefits'    && renderBenefitsTab()}
        </Animated.View>
      )}

      {/* All modals */}
      {renderAddEmpModal()}
      {renderEmpDetailModal()}
      {renderLeaveApproveModal()}
      {renderProcessPayrollModal()}
      {renderScheduleReviewModal()}
      {renderPostJobModal()}
      {renderIssueWarningModal()}
      {renderNotifModal()}
      {renderCreateOnboardModal()}
      {renderCreateExitModal()}
      {renderCreateBenefitModal()}
      {renderCreatePolicyModal()}
      {renderBulkModal()}
    </SafeAreaView>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const sty = StyleSheet.create({
  container:   { flex: 1, backgroundColor: '#F8FAFC' },
  header:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14, paddingTop: Platform.OS === 'android' ? 14 : 10 },
  tabScroll:   { flex: 1, paddingHorizontal: 16, paddingTop: 16 },
  sectionCard: { backgroundColor: '#fff', borderRadius: 16, padding: 16, marginBottom: 14, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 4, elevation: 2 },
  sectionTitle:{ fontSize: 16, fontWeight: '800', color: '#111827' },
  seeAll:      { fontSize: 13, color: '#0e4e96', fontWeight: '700' },
  statCard:    { backgroundColor: '#fff', borderRadius: 14, padding: 14, borderLeftWidth: 4, flexDirection: 'row', alignItems: 'center', gap: 8, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.05, shadowRadius: 3, elevation: 2, marginBottom: 0 },
  statValue:   { fontSize: 22, fontWeight: '900', color: '#111827', lineHeight: 26 },
  statTitle:   { fontSize: 11, color: '#6B7280', fontWeight: '600', marginTop: 2 },
  statSubtitle:{ fontSize: 10, color: '#9CA3AF', marginTop: 1 },
  empCard:     { backgroundColor: '#fff', borderRadius: 16, padding: 14, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 3 },
  leaveCard:   { backgroundColor: '#fff', borderRadius: 16, padding: 14, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 4, elevation: 3 },
});

export default HRDashboardScreen;